import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import VisitsService from "../../../services/VisitsService";
import httpInstance from "../../../services/config";
import Alert from "../../../components/UI/ConfirmBox/Alert";
import { SECTION_COLORS, SECTION_ICONS } from "./recordingModalIcons";
import "./TranscriptionRecordingModal.css";
import {
  VISIT_DOC_CHECKLIST_SEED,
  primaryChecklistIdForAiSection,
  sectionLabelsHaveMeaningfulContent,
  buildChecklistFromNotesAndTranscripts,
} from "../../../utils/transcriptionUtils";
import {
  saveDraftToIndexedDB,
  loadDraftFromIndexedDB,
  deleteDraftFromIndexedDB,
} from "../../../utils/draftStorage";

/** Timeslice (ms) so MediaRecorder emits chunks during recording — enables Play while paused. */
const MEDIA_RECORDER_TIMESLICE_MS = 500;

/** Per-segment recording cap (seconds); auto-pause when reached. */
const MAX_SEGMENT_DURATION_SEC = 300;

/** Show amber warning this many seconds before the segment cap. */
const SEGMENT_WARNING_DURATION_SEC = 240;

/** Minimum blob size (bytes) before uploading a pause draft to the server. */
const MIN_DRAFT_UPLOAD_BYTES = 256;

/**
 * Purpose: After requestData(), wait for MediaRecorder to flush chunks before pause/upload.
 * Inputs: recorder (MediaRecorder), waitMs (max wait)
 * Output: Promise<void>
 */
const waitForRecorderDataFlush = (recorder, waitMs = 450) =>
  new Promise((resolve) => {
    if (!recorder || recorder.state === "inactive") {
      resolve();
      return;
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      recorder.removeEventListener("dataavailable", onData);
      clearTimeout(timerId);
      resolve();
    };
    const onData = () => finish();
    recorder.addEventListener("dataavailable", onData);
    const timerId = setTimeout(finish, waitMs);
    try {
      if (typeof recorder.requestData === "function") {
        recorder.requestData();
      }
    } catch {
      finish();
    }
  });

/** Pre-upload: minimum decoded audio duration (seconds). */
const VOICE_MIN_DURATION_SEC = 0.85;

/** Pre-upload: minimum peak sliding-window RMS (rough proxy for audible speech). */
const VOICE_MIN_MAX_RMS = 0.014;

/** Pre-upload: for recordings at least this long, require at least this fraction of windows above RMS threshold. */
const VOICE_ACTIVE_RATIO_LONG_SEC = 2.5;
const VOICE_MIN_ACTIVE_RATIO = 0.04;
/** Pre-upload: minimum aggregate voiced time (seconds) from RMS windows; blocks silent/noise-only mobile clips. */
const VOICE_MIN_ACTIVE_SPEECH_SEC = 0.28;

/** Title shown above chart-note PDF download acknowledgment (legal / product copy). */
const AI_CHART_NOTE_DOWNLOAD_ACK_TITLE =
  "AI-Assisted Documentation & Orders – Provider Acknowledgment";

/** Opening paragraphs for AI chart-note download acknowledgment. */
const AI_CHART_NOTE_DOWNLOAD_ACK_INTRO_PARAGRAPHS = [
  "I acknowledge that this SnapEHR may use artificial intelligence (AI) to generate or suggest clinical documentation and orders.",
  "I understand and agree that:",
];

/** Bulleted understandings for AI chart-note download acknowledgment. */
const AI_CHART_NOTE_DOWNLOAD_ACK_BULLETS = [
  "AI-generated content is not always accurate, complete, or clinically appropriate.",
  "AI is a support tool only and must not be relied upon for clinical decision-making.",
  "Standard medical practice and independent clinical judgment must be applied at all times.",
  "I have personally reviewed and verified all documentation and orders.",
];

/** Closing paragraphs for AI chart-note download acknowledgment. */
const AI_CHART_NOTE_DOWNLOAD_ACK_CLOSING_PARAGRAPHS = [
  "I accept full responsibility for all final clinical content, decisions, and patient care.",
  "I further agree that the SnapEHR and its affiliates are not responsible or liable for any errors, omissions, or outcomes related to the use of AI-assisted features.",
];

/** Checkbox label — user must check before PDF download proceeds. */
const AI_CHART_NOTE_DOWNLOAD_ACK_CHECKBOX_LABEL =
  "I have read the acknowledgment above and agree to proceed with download.";

/** DOM id prefix for clinical note section anchors (checklist scroll-to-section). */
const SNAP_CLINICAL_SECTION_ID_PREFIX = "snap-voice-section";

/** Footer hint copy per workflow state. */
const WORKFLOW_FOOTER_HINTS = {
  idle: "Tap Start Recording to capture the visit. Audio is saved in segments when you pause.",
  recording: "Pause when finished with this segment. You can record multiple segments before generating the note.",
  paused:
    "Generate Note sends all segments to AI for transcription and merges them into one clinical note.",
  generating: "Please wait — your audio is being transcribed and the clinical note is being generated.",
  generated:
    "Record More adds another segment and merges it into the note on the next Generate. Finalize locks the chart and pushes to EMR.",
  finalized: "This chart note is locked. No further recording or edits are allowed.",
};

/** EMR sync banner copy + modifier class (synced | pending | failed). */
const EMR_SYNC_BANNER_BY_STATUS = {
  synced: {
    label: "✓ Chart Note Finalized & Synced to EMR",
    modifier: "synced",
  },
  pending: {
    label: "🔄 Chart Note Finalized — EMR sync in progress, this may take a few minutes.",
    modifier: "pending",
  },
  failed: {
    label: "⚠ Finalized — EMR Sync Failed",
    modifier: "failed",
  },
};

/**
 * Purpose: Maps API fields (note detail + visit-voice-status) to EMR sync banner state.
 * Inputs: detail (getAiNoteById payload), voiceStatus (visit-voice-status payload, optional)
 * Output: 'synced' | 'pending' | 'failed' | null
 */
function resolveEmrSyncDisplayStatus(detail, voiceStatus) {
  const mappingPending =
    voiceStatus?.finalize_mapping_pending === true ||
    detail?.finalize_mapping_pending === true;

  // Prefer API-resolved fields (same as backend resolveEmrSyncStatusForVisit); meta alone can be stale.
  const raw =
    detail?.emr_sync_status ??
    voiceStatus?.emr_sync_status ??
    detail?.meta?.emr_sync_status ??
    null;

  if (raw != null && String(raw).trim() !== "") {
    const s = String(raw).trim().toLowerCase();
    if (["synced", "success", "completed", "complete", "done"].includes(s)) {
      return "synced";
    }
    if (["pending", "in_progress", "in progress", "processing", "queued", "queue"].includes(s)) {
      // Match backend: only treat as synced when completion timestamp is set, not cache absence alone.
      const completedAt =
        detail?.meta?.emr_sync_completed_at ?? voiceStatus?.meta?.emr_sync_completed_at ?? null;
      if (!mappingPending && completedAt) {
        return "synced";
      }
      return "pending";
    }
    if (["failed", "failure", "error"].includes(s)) {
      return "failed";
    }
  }

  if (
    voiceStatus?.finalize_mapping_failed === true ||
    detail?.finalize_mapping_failed === true ||
    detail?.meta?.emr_sync_failed === true
  ) {
    return "failed";
  }

  if (mappingPending) {
    return "pending";
  }

  if (detail?.is_finalized || detail?.finalized_at) {
    return "synced";
  }

  return null;
}

/**
 * @param {object} p
 * @returns {'idle'|'recording'|'paused'|'generating'|'generated'|'finalized'}
 */
function deriveWorkflowState(p) {
  if (p.isFinalizedNote) return "finalized";
  if (p.isSubmitting || p.pipelinePhase !== "idle" || p.recordingPhase === "processing") {
    return "generating";
  }
  if (p.recordingPhase === "recording" && p.isRecording && !p.isPaused) return "recording";
  if (p.recordingPhase === "paused" || p.isPaused) return "paused";
  if (p.mergedNoteId && p.hasNoteContent) return "generated";
  return "idle";
}

/**
 * Purpose: Scrolls a clinical note section into view inside the modal's `.snap-panel` scroll container.
 * Inputs: sectionDomId (string) — full element id e.g. `snap-voice-section-proc`
 * Output: void
 * Side effects: Scrolls the `.snap-panel` ancestor that owns the overflow; focuses the target for a11y
 */
function scrollSnapClinicalSectionIntoView(sectionDomId) {
  const el = document.getElementById(sectionDomId);
  if (!el) return;

  const scrollParent = el.closest(".snap-panel");
  if (scrollParent) {
    const parentRect = scrollParent.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const pad = 8;
    const nextTop = scrollParent.scrollTop + (elRect.top - parentRect.top) - pad;
    scrollParent.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  if (typeof el.focus === "function") {
    try {
      el.focus({ preventScroll: true });
    } catch {
      /* ignore */
    }
  }
}


/**
 * Purpose: Promise-based delay for async polling loops.
 * Inputs: ms (number)
 * Output: Promise<void>
 */
function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Purpose: Groups AI note sections into static visit buckets (one primary bucket per AI section).
 * Inputs: notesCollection
 * Output: Map staticId -> array of AI section objects
 */
function bucketAiSectionsByStaticId(notesCollection) {
  /** @type {Record<string, Array<{ notesIndex: number, sectionName: string, label: unknown[] }>>} */
  const buckets = {};
  for (const row of VISIT_DOC_CHECKLIST_SEED) {
    buckets[row.id] = [];
  }
  if (!Array.isArray(notesCollection)) return buckets;
  notesCollection.forEach((sec, notesIndex) => {
    const labels = sec?.label || [];
    if (!sectionLabelsHaveMeaningfulContent(labels)) return;
    const id = primaryChecklistIdForAiSection(sec?.sectionName || "");
    if (id && buckets[id]) {
      buckets[id].push({
        notesIndex,
        sectionName: sec.sectionName || "Section",
        label: Array.isArray(labels) ? labels : [],
      });
    }
  });
  return buckets;
}

/**
 * Purpose: Converts stored clinical HTML to plain text for textarea editing.
 * Inputs: html (string)
 * Output: string (plain text, newlines preserved where possible)
 */
function clinicalHtmlToEditableText(html) {
  const raw = String(html || "").trim();
  if (!raw) return "";
  if (typeof document === "undefined") {
    return raw.replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n\n").replace(/<[^>]+>/g, "");
  }
  const d = document.createElement("div");
  d.innerHTML = raw;
  return (d.textContent || d.innerText || "").replace(/\u00a0/g, " ");
}

/**
 * Purpose: Converts plain textarea lines back to safe paragraph HTML for the API.
 * Inputs: text (string)
 * Output: HTML string
 */
function editableTextToClinicalHtml(text) {
  const lines = String(text || "").split(/\n/);
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return lines.map((line) => `<p>${escape(line)}</p>`).join("");
}

/**
 * Purpose: Detects failed VisitsService / HTTP envelope results.
 * Inputs: data (API return value)
 * Output: boolean — true if save/load failed
 */
function isFailedVoiceServiceResult(data) {
  return (
    data == null ||
    (typeof data === "object" &&
      (data.status === "ERROR" ||
        data.failed === true ||
        (typeof data.code === "number" && data.code >= 400)))
  );
}

/**
 * Purpose: Tooltip/disabled reason for section Edit; null means Edit may start (subject to merged note id + pipeline).
 * Inputs: mergedNoteId, pipelineBusy, finalizingRecording
 * Output: string | null
 */
function getClinicalSectionEditStartBlockReason({
  mergedNoteId,
  pipelineBusy,
  finalizingRecording,
  isFinalizedNote,
}) {
  if (isFinalizedNote) {
    return "Chart note is finalized and read-only.";
  }
  if (!mergedNoteId) {
    return "Generate a merged clinical note first (pause recording, then Generate Clinical Note).";
  }
  if (pipelineBusy) {
    return "Wait for transcription or note generation to finish.";
  }
  if (finalizingRecording) {
    return "Finish saving the recording before editing.";
  }
  return null;
}

/**
 * Purpose: Builds one visit-level transcript string from completed audio rows (oldest first).
 * Inputs: audioList (array from getAudioList)
 * Output: string
 */
function effectiveTranscriptTextFromAudioRow(row) {
  const stripped = String(row?.transcript_text_stripped || "").trim();
  if (stripped) return stripped;
  return String(row?.transcript_text || "").trim();
}

function buildMergedTranscriptFromAudioList(audioList) {
  if (!Array.isArray(audioList) || audioList.length === 0) return "";
  const completed = audioList
    .filter(
      (r) =>
        String(r.status || "").toLowerCase() === "completed" &&
        effectiveTranscriptTextFromAudioRow(r)
    )
    .sort((a, b) => Number(a.id) - Number(b.id));
  return completed.map((r) => effectiveTranscriptTextFromAudioRow(r)).join("\n\n---\n\n");
}

function writeStringToDataView(view, offset, str) {
  for (let i = 0; i < str.length; i += 1) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

/**
 * Purpose: Encodes an AudioBuffer as 16-bit PCM WAV for upload compatibility.
 * Inputs: audioBuffer (AudioBuffer)
 * Output: Blob (audio/wav)
 */
function audioBufferToWavBlob(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const length = audioBuffer.length;
  const dataSize = length * numChannels * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeStringToDataView(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStringToDataView(view, 8, "WAVE");
  writeStringToDataView(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeStringToDataView(view, 36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < length; i += 1) {
    for (let ch = 0; ch < numChannels; ch += 1) {
      const s = Math.max(-1, Math.min(1, audioBuffer.getChannelData(ch)[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      offset += 2;
    }
  }
  return new Blob([buffer], { type: "audio/wav" });
}

function detectSpeechSampleBounds(channelData, sampleRate) {
  const win = Math.max(256, Math.floor(sampleRate * 0.025));
  const hop = Math.max(64, Math.floor(win / 4));
  const threshold = 0.012;
  const minStreak = Math.max(2, Math.ceil((0.05 * sampleRate) / hop));

  let startSample = 0;
  let streak = 0;
  for (let i = 0; i + win <= channelData.length; i += hop) {
    let sum = 0;
    for (let j = 0; j < win; j += 1) {
      const v = channelData[i + j];
      sum += v * v;
    }
    const rms = Math.sqrt(sum / win);
    if (rms > threshold) {
      streak += 1;
      if (streak >= minStreak) {
        startSample = Math.max(0, i - (minStreak - 1) * hop);
        break;
      }
    } else {
      streak = 0;
    }
  }

  streak = 0;
  let endSample = channelData.length;
  for (let i = channelData.length - win; i >= startSample; i -= hop) {
    let sum = 0;
    for (let j = 0; j < win; j += 1) {
      const v = channelData[i + j];
      sum += v * v;
    }
    const rms = Math.sqrt(sum / win);
    if (rms > threshold) {
      streak += 1;
      if (streak >= minStreak) {
        endSample = Math.min(channelData.length, i + win + (minStreak - 1) * hop);
        break;
      }
    } else {
      streak = 0;
    }
  }

  if (endSample <= startSample) {
    return { startSample: 0, endSample: channelData.length };
  }
  return { startSample, endSample };
}

/**
 * Purpose: Sliding-window RMS stats to gate uploads (reject silence / near-silence before transcription).
 * Inputs: channelData (Float32Array), sampleRate (number)
 * Output: { maxRms, activeRatio, activeSpeechSec, durationSec }
 */
function measureVoiceActivityMetrics(channelData, sampleRate) {
  const win = Math.max(256, Math.floor(sampleRate * 0.025));
  const hop = Math.max(64, Math.floor(win / 4));
  const thr = 0.012;
  let maxRms = 0;
  let activeWindows = 0;
  let totalWindows = 0;
  for (let i = 0; i + win <= channelData.length; i += hop) {
    let sum = 0;
    for (let j = 0; j < win; j += 1) {
      const v = channelData[i + j];
      sum += v * v;
    }
    const rms = Math.sqrt(sum / win);
    maxRms = Math.max(maxRms, rms);
    if (rms > thr) activeWindows += 1;
    totalWindows += 1;
  }
  const activeRatio = totalWindows > 0 ? activeWindows / totalWindows : 0;
  return {
    maxRms,
    activeRatio,
    activeSpeechSec: activeRatio * (channelData.length / sampleRate),
    durationSec: channelData.length / sampleRate,
  };
}

/**
 * Purpose: Client-side validation before audio/submit — blocks empty mic, digital silence, orclips with almost no speech.
 * Inputs: blob (Blob|File), Toast (optional UI feedback)
 * Output: Promise<boolean> — true if upload should proceed
 */
async function assertBlobHasAudibleSpeech(blob, Toast) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) {
    Toast?.error?.("Audio check is not available in this browser.");
    return false;
  }
  const ctx = new Ctx();
  try {
    const ab = await blob.arrayBuffer();
    if (!ab || ab.byteLength < 64) {
      Toast?.error?.("Recording is empty.");
      return false;
    }
    let audioBuffer;
    try {
      audioBuffer = await ctx.decodeAudioData(ab.slice(0));
    } catch {
      Toast?.error?.("Could not read this recording. Try again.");
      return false;
    }
    const ch0 = audioBuffer.getChannelData(0);
    const { maxRms, activeRatio, activeSpeechSec, durationSec } = measureVoiceActivityMetrics(
      ch0,
      audioBuffer.sampleRate
    );

    if (!Number.isFinite(durationSec) || durationSec < VOICE_MIN_DURATION_SEC) {
      Toast?.error?.("Recording is too short. Speak for at least one second, then use Accept / Chart.");
      return false;
    }
    if (maxRms < VOICE_MIN_MAX_RMS) {
      Toast?.error?.("No clear voice was detected. Check the microphone and try again.");
      return false;
    }
    if (activeSpeechSec < VOICE_MIN_ACTIVE_SPEECH_SEC) {
      Toast?.error?.("No meaningful speech was detected. Please speak clearly, then try again.");
      return false;
    }
    if (durationSec >= VOICE_ACTIVE_RATIO_LONG_SEC && activeRatio < VOICE_MIN_ACTIVE_RATIO) {
      Toast?.error?.("Almost no speech in this clip. Please record while speaking, then try again.");
      return false;
    }
    return true;
  } finally {
    try {
      await ctx.close();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Purpose: Trims leading/trailing silence from a recorded blob before upload.
 * Inputs: blob (Blob, e.g. webm from MediaRecorder)
 * Output: Promise resolving to { blob, useOriginal, trimOffsetRatio, durationSec }
 */
/**
 * Purpose: Decodes a blob and returns duration in seconds (for draft timer restore).
 */
async function getBlobDurationSec(blob) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx || !blob?.size) return 0;
  const ctx = new Ctx();
  try {
    const ab = await blob.arrayBuffer();
    const audioBuffer = await ctx.decodeAudioData(ab.slice(0));
    return Number.isFinite(audioBuffer.duration) ? audioBuffer.duration : 0;
  } catch {
    return 0;
  } finally {
    try {
      await ctx.close();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Purpose: Builds normalized peak heights (0–1) for static paused/playback waveform UI.
 */
async function computeWaveformPeaksFromBlob(blob, barCount = 48) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx || !blob?.size) return null;
  const ctx = new Ctx();
  try {
    const ab = await blob.arrayBuffer();
    const audioBuffer = await ctx.decodeAudioData(ab.slice(0));
    const ch0 = audioBuffer.getChannelData(0);
    const blockSize = Math.max(1, Math.floor(ch0.length / barCount));
    const peaks = [];
    for (let i = 0; i < barCount; i += 1) {
      let peak = 0;
      const start = i * blockSize;
      for (let j = 0; j < blockSize && start + j < ch0.length; j += 1) {
        peak = Math.max(peak, Math.abs(ch0[start + j] || 0));
      }
      peaks.push(peak);
    }
    const max = Math.max(...peaks, 0.001);
    return peaks.map((p) => p / max);
  } catch {
    return null;
  } finally {
    try {
      await ctx.close();
    } catch {
      /* ignore */
    }
  }
}

async function trimSilenceFromRecordedBlob(blob) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  let audioBuffer;
  try {
    const ab = await blob.arrayBuffer();
    audioBuffer = await ctx.decodeAudioData(ab.slice(0));
  } catch {
    await ctx.close();
    return { blob, useOriginal: true, trimOffsetRatio: 0, durationSec: 0 };
  }

  const ch0 = audioBuffer.getChannelData(0);
  const { startSample, endSample } = detectSpeechSampleBounds(ch0, audioBuffer.sampleRate);
  const durationSec = audioBuffer.duration;
  const trimOffsetRatio = ch0.length > 0 ? startSample / ch0.length : 0;

  if (endSample - startSample < audioBuffer.sampleRate * 0.05) {
    await ctx.close();
    return { blob, useOriginal: true, trimOffsetRatio, durationSec };
  }

  const len = endSample - startSample;
  const trimmed = ctx.createBuffer(audioBuffer.numberOfChannels, len, audioBuffer.sampleRate);
  for (let c = 0; c < audioBuffer.numberOfChannels; c += 1) {
    trimmed.getChannelData(c).set(audioBuffer.getChannelData(c).subarray(startSample, endSample));
  }
  await ctx.close();

  const wavBlob = audioBufferToWavBlob(trimmed);
  return { blob: wavBlob, useOriginal: false, trimOffsetRatio, durationSec: trimmed.duration };
}

function formatTime(totalSeconds) {
  const s = Number.isFinite(totalSeconds) ? Math.max(0, totalSeconds) : 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m < 10 ? "0" + m : m}:${sec < 10 ? "0" + sec : sec}`;
}

/**
 * Purpose: Formats patient DOB for header meta (mm/dd/yyyy).
 * Inputs: raw (string ISO or mm/dd/yyyy or empty)
 * Output: string
 */
function formatDOB(raw) {
  if (!raw) return "";
  const t = String(raw).trim();
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(t)) return t;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return t;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

/**
 * Purpose: Flat “null” baseline when not capturing; live bar heights from Web Audio analyser while recording (voice-reactive).
 * Inputs: analyser (AnalyserNode|null), reactive (true only while mic is actively recording, not paused), barCount, small
 * Output: React element
 * Side effects: requestAnimationFrame loop while reactive
 */
function VoiceWaveform({
  analyser,
  reactive,
  barCount = 48,
  small = false,
  staticPeaks = null,
  playbackProgress = 0,
}) {
  const [bars, setBars] = useState(() => Array.from({ length: barCount }, () => 2));
  const rafRef = useRef(null);

  const staticPeakList = useMemo(() => {
    if (!Array.isArray(staticPeaks) || staticPeaks.length === 0) return null;
    if (staticPeaks.length === barCount) return staticPeaks;
    const out = [];
    for (let i = 0; i < barCount; i += 1) {
      const srcIdx = Math.floor((i / barCount) * staticPeaks.length);
      out.push(staticPeaks[srcIdx] ?? 0);
    }
    return out;
  }, [staticPeaks, barCount]);

  const showStaticWave = staticPeakList && !reactive;

  useEffect(() => {
    if (showStaticWave) {
      return undefined;
    }
    if (!reactive) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setBars(Array.from({ length: barCount }, () => 2));
      return undefined;
    }

    if (analyser) {
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const step = Math.floor(bufferLength / barCount) || 1;

      const tick = () => {
        analyser.getByteFrequencyData(dataArray);
        const next = [];
        for (let i = 0; i < barCount; i += 1) {
          let sum = 0;
          for (let j = 0; j < step; j += 1) {
            sum += dataArray[Math.min(i * step + j, bufferLength - 1)] ?? 0;
          }
          const avg = sum / step;
          const norm = avg / 255;
          next.push(small ? 2 + norm * 12 : 2 + norm * 27);
        }
        setBars(next);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } else {
      /** Playback fallback: keep bars animated even when no live analyser is attached. */
      const base = small ? 5 : 10;
      const amp = small ? 7 : 15;
      const tick = () => {
        const t = Date.now() / 220;
        const next = Array.from({ length: barCount }, (_, i) => {
          const wobble = Math.sin(t + i * 0.4) + Math.sin(t * 0.7 + i * 0.9) * 0.5;
          return Math.max(2, base + ((wobble + 1.5) / 2.5) * amp);
        });
        setBars(next);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [analyser, reactive, barCount, small, showStaticWave]);

  const cls = small ? "snap-mini-wave" : "snap-wave";
  const barCls = small ? "snap-mini-wave-bar" : "snap-wave-bar";
  const max = small ? 14 : 30;

  if (showStaticWave) {
    const progressIdx = Math.floor(Math.max(0, Math.min(1, playbackProgress)) * staticPeakList.length);
    return (
      <div className={`${cls} snap-wave-static`} aria-hidden="true">
        {staticPeakList.map((norm, i) => {
          const h = small ? 2 + norm * 12 : 2 + norm * 27;
          const played = i <= progressIdx;
          return (
            <div
              key={i}
              className={barCls}
              style={{
                height: `${h}px`,
                opacity: played ? 0.45 + (h / max) * 0.55 : 0.22 + norm * 0.2,
              }}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className={`${cls}${reactive ? "" : " snap-wave-idle"}`} aria-hidden="true">
      {bars.map((h, i) => (
        <div
          key={i}
          className={barCls}
          style={{ height: `${h}px`, opacity: reactive ? 0.35 + (h / max) * 0.65 : 0.2 }}
        />
      ))}
    </div>
  );
}

/** Inline SVG: play triangle for waveform toolbar */
function IconPlayGlyph({ playing }) {
  if (playing) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <rect x="6" y="5" width="4" height="14" rx="1" />
        <rect x="14" y="5" width="4" height="14" rx="1" />
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

/** Inline SVG: download for chart note PDF */
function IconDownloadGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 3v12m0 0l4-4m-4 4L8 11" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 19h16" strokeLinecap="round" />
    </svg>
  );
}

/** Inline SVG: pencil — edit clinical section */
function IconEditGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Purpose: Renders visit documentation sections with per-section edit/save; Edit is always shown, disabled when not startable.
 * Inputs: notesCollection, editingStaticId, sectionEditStartBlockReason (null = may edit), saveBusy, handlers
 * Output: React element; section roots keep id `snap-voice-section-{checklistId}` for checklist scroll
 */
function StaticVisitNotePanel({
  notesCollection,
  editingStaticId,
  sectionEditStartBlockReason,
  saveBusy,
  onStartEditSection,
  onCancelEditSection,
  onSaveSection,
  onDraftFieldPlainTextChange,
}) {
  const buckets = useMemo(() => bucketAiSectionsByStaticId(notesCollection), [notesCollection]);
  const visibleRows = useMemo(
    () => VISIT_DOC_CHECKLIST_SEED.filter((row) => (buckets[row.id] || []).length > 0),
    [buckets]
  );

  if (visibleRows.length === 0) {
    return (
      <div className="snap-transcript-empty">
        Record and choose <strong>Accept / Chart</strong> to build a clinical note from your visit audio. Multiple
        recordings merge into one note.
      </div>
    );
  }

  return (
    <div className="snap-clinical-note-body snap-static-sections-root snap-note-format-root">
      {visibleRows.map((row) => {
        const blocks = buckets[row.id] || [];
        const isEditingRow = editingStaticId === row.id;
        const editLockedElsewhere = editingStaticId != null && editingStaticId !== row.id;
        const cannotStartEdit = sectionEditStartBlockReason !== null;

        return (
          <div
            key={row.id}
            id={`${SNAP_CLINICAL_SECTION_ID_PREFIX}-${row.id}`}
            className="snap-static-doc-section-wrap"
            tabIndex={-1}
          >
            <div className="snap-note-section-heading-row">
              <h3 className="snap-note-section-heading">{row.label}</h3>
              <div className="snap-note-section-actions">
                {isEditingRow ? (
                  <>
                    <button
                      type="button"
                      className="snap-note-section-action-btn"
                      onClick={onCancelEditSection}
                      disabled={saveBusy}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="snap-note-section-action-btn snap-note-section-action-primary"
                      onClick={() => onSaveSection(row.id)}
                      disabled={saveBusy}
                    >
                      {saveBusy ? "Saving…" : "Save"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="snap-note-section-action-btn snap-note-section-action-icon"
                    onClick={() => onStartEditSection(row.id)}
                    disabled={editLockedElsewhere || cannotStartEdit}
                    aria-label={`Edit ${row.label}`}
                    title={
                      editLockedElsewhere
                        ? "Finish editing the other section first"
                        : cannotStartEdit
                          ? sectionEditStartBlockReason
                          : `Edit ${row.label}`
                    }
                  >
                    <IconEditGlyph />
                  </button>
                )}
              </div>
            </div>
            <div className="snap-note-section-block">
              {blocks.map((sec) => (
                <div key={`${row.id}-${sec.notesIndex}`} className="snap-ai-subsection">
                  {blocks.length > 1 && sec.sectionName ? (
                    <div className="snap-ai-subsection-name">{sec.sectionName}</div>
                  ) : null}
                  {(sec.label || []).map((lab, j) => {
                    const plain = clinicalHtmlToEditableText(lab?.value);
                    const rows = Math.min(22, Math.max(4, plain.split("\n").length + 2));
                    return (
                      <div key={`${sec.notesIndex}-${j}`} className="snap-note-field-row">
                        {lab.name ? (
                          <span className="snap-note-field-name">{String(lab.name).replace(/:\s*$/, "")}:</span>
                        ) : null}
                        {isEditingRow ? (
                          <textarea
                            className="snap-note-field-textarea"
                            value={plain}
                            rows={rows}
                            onChange={(e) => onDraftFieldPlainTextChange(sec.notesIndex, j, e.target.value)}
                            aria-label={lab.name ? String(lab.name) : "Clinical note field"}
                          />
                        ) : (
                          <div
                            className="snap-note-field-body"
                            dangerouslySetInnerHTML={{
                              __html: String(lab.value || "").replace(/<\/script/gi, "<\\/script"),
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Purpose: Live visit voice-first overlay — record, submit audio, wait for transcription, merged AI note without a separate “Generate” step.
 * Inputs: isOpen, visitId, visitData, profile, onClose, Toast, Loader
 * Output: React element or null
 * Side effects: Mic access, polling, POST generate-merged-note, Escape to close (with confirm when active)
 */
export default function TranscriptionRecordingModal({
  isOpen,
  visitId,
  visitData,
  profile,
  onClose,
  Toast,
  Loader,
}) {
  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 767 ? "checklist" : "clinical"
  );
  const [elapsed, setElapsed] = useState(0);
  /** null | 'warning' | 'limit_reached' — per-segment duration banners inside the record card */
  const [segmentLimitBanner, setSegmentLimitBanner] = useState(null);
  /** Pulse "Generate Clinical Note" after auto-pause at segment cap */
  const [generatePulseActive, setGeneratePulseActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checklist, setChecklist] = useState(() => VISIT_DOC_CHECKLIST_SEED.map((c) => ({ ...c })));
  const [finalizingRecording, setFinalizingRecording] = useState(false);
  /** idle | transcribing | generating | processing */
  const [pipelinePhase, setPipelinePhase] = useState("idle");
  const [notesCollection, setNotesCollection] = useState([]);
  const [combinedTranscript, setCombinedTranscript] = useState("");
  const [transcriptSegments, setTranscriptSegments] = useState([]);
  const [latestUploadedAudioId, setLatestUploadedAudioId] = useState(null);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  /** Last finalized / uploaded clip for replay (survives reset after Stop & Generate). */
  const [playbackBlob, setPlaybackBlob] = useState(null);
  const [vizAnalyser, setVizAnalyser] = useState(null);
  const [chunkVersion, setChunkVersion] = useState(0);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [previewCurrentTimeSec, setPreviewCurrentTimeSec] = useState(0);
  /** Which saved transcript segment's audio is playing (server audio_records id). */
  const [playingTranscriptSegmentId, setPlayingTranscriptSegmentId] = useState(null);
  const [transcriptPlayLoadingId, setTranscriptPlayLoadingId] = useState(null);
  /** Server id of merged transcript_note for PDF download (`GET audio/notes/{id}/download`). */
  const [mergedNoteId, setMergedNoteId] = useState(null);
  /** Provider must acknowledge AI disclaimer before PDF download or chart finalize. */
  const [aiDownloadAckOpen, setAiDownloadAckOpen] = useState(false);
  const [aiDownloadAckChecked, setAiDownloadAckChecked] = useState(false);
  /** 'download' | 'finalize' — same acknowledgment modal, different confirm action. */
  const [aiAckPurpose, setAiAckPurpose] = useState("download");
  const [isFinalizedNote, setIsFinalizedNote] = useState(false);
  /** 'synced' | 'pending' | 'failed' — EMR finalize banner after lock / on reopen */
  const [emrSyncDisplayStatus, setEmrSyncDisplayStatus] = useState(null);
  const [finalizeNoteBusy, setFinalizeNoteBusy] = useState(false);
  /** Single-section edit of merged `notes_collection` (voice modal clinical tab). */
  const [clinicalSectionEditingId, setClinicalSectionEditingId] = useState(null);
  const [notesCollectionDraft, setNotesCollectionDraft] = useState(null);
  const [clinicalSectionSaveBusy, setClinicalSectionSaveBusy] = useState(false);
  /** True while initial GET audio/list + notes + merged note fetch runs (modal open). */
  const [visitDocumentationLoading, setVisitDocumentationLoading] = useState(false);
  /** idle | recording | paused | processing — recording-tab button phase */
  const [recordingPhase, setRecordingPhase] = useState("idle");
  const [draftBlob, setDraftBlob] = useState(null);
  const [draftLoadedFromCache, setDraftLoadedFromCache] = useState(false);
  const [draftWaveformPeaks, setDraftWaveformPeaks] = useState(null);
  const [rerecordConfirmOpen, setRerecordConfirmOpen] = useState(false);
  /** UI segment badges for paused state: { id, label, durationSec } */
  const [recordingSegments, setRecordingSegments] = useState([]);

  const segmentStartRef = useRef(0);

  /*
   * analysis (pre-refactor inventory):
   * Q1: Start (handleStart), Pause/Resume toggle (handlePause), Stop & Generate (handleStopAndGenerate), Cancel (handleCancel/requestClose)
   * Q2: isRecording, isPaused, finalizingRecording, pipelinePhase, recordedBlob, elapsed, chunkVersion, isSubmitting
   * Q3: No — POST audio/submit only accepts file + visit_id; always queues PrepareAudioTranscriptionJob
   * Q4: No draft fields — list returns audio_records; visit-voice-status uses uploaded/processing/completed/failed only
   * Q5: Yes — MediaRecorder.pause() / resume() in handlePause
   * Q6: Stop & Generate stops recorder, waitForTrimmedBlob, runAudioPipelineFromBlob (submit + poll); Pause only pauses timer/recorder
   * Q7: audioChunksRef accumulates MediaRecorder ondataavailable chunks
   * Q8: audio/incoming/{id}.ext and audio/{id}.wav — audio/drafts/{visit_id}/draft.webm is additive
   * Q9: status string column — uploaded, processing, completed, failed (no draft)
   * Q10: PrepareAudioTranscriptionJob::dispatch($id)->afterResponse() on submit
   */

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const recordedBlobRef = useRef(null);
  const isRecordingRef = useRef(false);
  const vizAudioContextRef = useRef(null);
  const vizSourceRef = useRef(null);
  const audioPreviewRef = useRef(null);
  const previewObjectUrlRef = useRef(null);
  const playbackBlobVisitIdRef = useRef(null);
  const transcriptAudioRef = useRef(null);
  const transcriptObjectUrlRef = useRef(null);
  const voicePollAbortRef = useRef(false);
  const consecutiveNullCountRef = useRef(0);
  const suppressPreviewOnStopRef = useRef(false);
  const draftUploadInFlightRef = useRef(false);
  const elapsedRef = useRef(0);
  const segmentLimitAutoPauseFiredRef = useRef(false);
  const pauseRecordingRef = useRef(() => {});
  const playbackVizContextRef = useRef(null);
  const previewMediaElementSourceRef = useRef(null);
  const previewPlaybackAnalyserRef = useRef(null);
  const previewDurationSecRef = useRef(0);
  const [audioPreviewInstanceKey, setAudioPreviewInstanceKey] = useState(0);

  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);

  /**
   * Purpose: Fire-and-forget server draft save on pause (does not block UI).
   */
  const uploadDraftToServerInBackground = useCallback(
    (blob) => {
      if (
        !visitId ||
        !blob ||
        blob.size < MIN_DRAFT_UPLOAD_BYTES ||
        draftUploadInFlightRef.current
      ) {
        return;
      }
      draftUploadInFlightRef.current = true;
      const formData = new FormData();
      formData.append("file", new File([blob], `draft-${visitId}.webm`, { type: blob.type || "audio/webm" }));
      formData.append("visit_id", visitId);
      void VisitsService.submitAudioDraft(formData)
        .catch((e) => console.error(e))
        .finally(() => {
          draftUploadInFlightRef.current = false;
        });
    },
    [visitId]
  );

  /**
   * Purpose: Derives duration + waveform peaks from a draft blob and updates UI state.
   */
  const applyDraftMediaMeta = useCallback(async (blob, storedDurationSec = 0, storedPeaks = null) => {
    let durationSec = Number(storedDurationSec) || 0;
    if (durationSec <= 0) {
      durationSec = await getBlobDurationSec(blob);
    }
    const rounded = Math.max(1, Math.ceil(durationSec || 0));
    previewDurationSecRef.current = rounded;
    setElapsed(rounded);

    let peaks = storedPeaks;
    if (!peaks?.length) {
      peaks = await computeWaveformPeaksFromBlob(blob, 48);
    }
    setDraftWaveformPeaks(peaks?.length ? peaks : null);
  }, []);

  /**
   * Purpose: Restores paused recording from IndexedDB, then server draft, when modal opens.
   */
  const recoverRecordingDraftOnOpen = useCallback(async () => {
    if (!visitId) return;
    let blob = null;
    let durationSec = 0;
    let waveformPeaks = null;

    const stored = await loadDraftFromIndexedDB(visitId);
    if (stored?.blob?.size) {
      blob = stored.blob;
      durationSec = stored.durationSec;
      waveformPeaks = stored.waveformPeaks;
    }

    if (!blob?.size) {
      const meta = await VisitsService.getAudioDraftMeta(visitId);
      if (meta?.id) {
        blob = await VisitsService.fetchAudioDraftStreamBlob(visitId);
        if (blob?.size) {
          const decodedDuration = await getBlobDurationSec(blob);
          const peaks = await computeWaveformPeaksFromBlob(blob, 48);
          durationSec = decodedDuration;
          waveformPeaks = peaks;
          await saveDraftToIndexedDB(visitId, blob, {
            durationSec: Math.max(1, Math.ceil(decodedDuration || 0)),
            waveformPeaks: peaks,
          });
        }
      }
    }

    if (blob && blob.size > 0) {
      await applyDraftMediaMeta(blob, durationSec, waveformPeaks);
      const draftDur = Math.max(1, Math.ceil(durationSec || 0));
      setRecordingSegments([{ id: 1, label: "Segment 1", durationSec: draftDur }]);
      segmentStartRef.current = draftDur;
      setDraftBlob(blob);
      setDraftLoadedFromCache(true);
      setRecordingPhase("paused");
      setIsPaused(true);
      setPlaybackBlob(blob);
      playbackBlobVisitIdRef.current = visitId;
      if (typeof Toast?.warning === "function") {
        Toast.warning("You have a paused recording. Resume or rerecord.");
      } else {
        Toast?.success?.("You have a paused recording. Resume or rerecord.");
      }
    } else {
      setDraftBlob(null);
      setDraftLoadedFromCache(false);
      setDraftWaveformPeaks(null);
      setElapsed(0);
    }
  }, [visitId, Toast, applyDraftMediaMeta]);

  useEffect(() => {
    voicePollAbortRef.current = !isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (closeConfirmOpen) {
      document.body.classList.add("snap-voice-recording-confirm-open");
    } else {
      document.body.classList.remove("snap-voice-recording-confirm-open");
    }
    return () => document.body.classList.remove("snap-voice-recording-confirm-open");
  }, [closeConfirmOpen]);

  useEffect(() => {
    if (!isOpen) {
      setVisitDocumentationLoading(false);
      setAiDownloadAckOpen(false);
      setAiDownloadAckChecked(false);
      setClinicalSectionEditingId(null);
      setNotesCollectionDraft(null);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      voicePollAbortRef.current = true;
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
      if (transcriptObjectUrlRef.current) {
        URL.revokeObjectURL(transcriptObjectUrlRef.current);
        transcriptObjectUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    recordedBlobRef.current = recordedBlob;
  }, [recordedBlob]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    if (!visitId) return;
    if (playbackBlobVisitIdRef.current == null) {
      playbackBlobVisitIdRef.current = visitId;
      return;
    }
    if (String(playbackBlobVisitIdRef.current) !== String(visitId)) {
      setPlaybackBlob(null);
      setPreviewCurrentTimeSec(0);
      playbackBlobVisitIdRef.current = visitId;
    }
  }, [visitId]);

  /**
   * Purpose: Loads merged clinical note and combined transcript when modal opens for a visit.
   * Inputs: visit id from props; optional `mergedNoteDetail` (note payload — skips list/details GETs);
   *        optional `mergedNoteIdHint` (loads `audio/notes/:id` only, skips `audio/notes?visit_id` when it succeeds).
   *        optional `showLoadingOverlay` — in-modal blocker until async work completes (use on open).
   * Output: none; updates notesCollection, checklist, combinedTranscript
   */
  const refreshVisitDocumentation = useCallback(async (options = {}) => {
    const { mergedNoteDetail, mergedNoteIdHint, showLoadingOverlay = false } = options;
    if (!visitId) return;
    if (showLoadingOverlay) {
      setVisitDocumentationLoading(true);
    }
    try {
      const audioList = await VisitsService.getAudioList(visitId);
      setCombinedTranscript(buildMergedTranscriptFromAudioList(audioList));
      const segments = (Array.isArray(audioList) ? audioList : [])
        .filter(
          (r) =>
            String(r.status || "").toLowerCase() === "completed" && String(r.transcript_text || "").trim()
        )
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map((r) => ({
          id: r.id,
          text: r.transcript_text,
          created_at: r.created_at,
          file_name: r.file_name,
          status: r.status,
        }));
      setTranscriptSegments(segments);

      const applyMergedDetail = (detail, voiceStatus = null) => {
        if (!detail?.id) {
          setMergedNoteId(null);
          setIsFinalizedNote(false);
          setEmrSyncDisplayStatus(null);
          setChecklist(buildChecklistFromNotesAndTranscripts([], segments));
          return;
        }
        setMergedNoteId(detail.id);
        const finalized = Boolean(detail.is_finalized || detail.finalized_at);
        setIsFinalizedNote(finalized);
        setEmrSyncDisplayStatus(
          finalized ? resolveEmrSyncDisplayStatus(detail, voiceStatus) : null
        );
        if (detail.notes_collection?.length) {
          setNotesCollection(detail.notes_collection);
          setChecklist(buildChecklistFromNotesAndTranscripts(detail.notes_collection, segments));
        } else {
          setNotesCollection([]);
          setChecklist(buildChecklistFromNotesAndTranscripts([], segments));
        }
      };

      if (mergedNoteDetail?.id) {
        let voiceStatus = null;
        if (mergedNoteDetail.is_finalized || mergedNoteDetail.finalized_at) {
          voiceStatus = await VisitsService.getVisitVoiceProcessingStatus(visitId);
        }
        applyMergedDetail(mergedNoteDetail, voiceStatus);
        return;
      }

      let detail = null;
      if (mergedNoteIdHint != null && mergedNoteIdHint !== "") {
        detail = await VisitsService.getAiNoteById(mergedNoteIdHint);
      }

      if (!detail?.id) {
        const noteRows = await VisitsService.getAiNotes(visitId);
        const rows = Array.isArray(noteRows) ? noteRows : [];
        const merged = rows.find((n) => n.is_merged_note);
        if (merged?.id) {
          detail = await VisitsService.getAiNoteById(merged.id);
        }
      }

      if (detail?.id) {
        let voiceStatus = null;
        if (detail.is_finalized || detail.finalized_at) {
          voiceStatus = await VisitsService.getVisitVoiceProcessingStatus(visitId);
        }
        applyMergedDetail(detail, voiceStatus);
        return;
      }

      setMergedNoteId(null);
      setIsFinalizedNote(false);
      setEmrSyncDisplayStatus(null);
      setChecklist(buildChecklistFromNotesAndTranscripts([], segments));
    } catch (e) {
      console.error(e);
    } finally {
      if (showLoadingOverlay) {
        setVisitDocumentationLoading(false);
      }
    }
  }, [visitId]);

  useEffect(() => {
    if (isOpen) {
      setChecklist(VISIT_DOC_CHECKLIST_SEED.map((c) => ({ ...c })));
      setActiveTab("clinical");
      setNotesCollection([]);
      setIsFinalizedNote(false);
      setEmrSyncDisplayStatus(null);
      setCombinedTranscript("");
      setFinalizingRecording(false);
      setPipelinePhase("idle");
      setRecordingPhase("idle");
      setDraftWaveformPeaks(null);
      setRerecordConfirmOpen(false);
      setCloseConfirmOpen(false);
      setTranscriptSegments([]);
      setLatestUploadedAudioId(null);
      setVizAnalyser(null);
      setIsPlayingPreview(false);
      setPreviewCurrentTimeSec(0);
      setPlayingTranscriptSegmentId(null);
      setTranscriptPlayLoadingId(null);
      setChunkVersion(0);
      setMergedNoteId(null);
      try {
        audioPreviewRef.current?.pause?.();
        if (previewObjectUrlRef.current) {
          URL.revokeObjectURL(previewObjectUrlRef.current);
          previewObjectUrlRef.current = null;
        }
        if (audioPreviewRef.current) {
          audioPreviewRef.current.src = "";
        }
        transcriptAudioRef.current?.pause?.();
        if (transcriptObjectUrlRef.current) {
          URL.revokeObjectURL(transcriptObjectUrlRef.current);
          transcriptObjectUrlRef.current = null;
        }
        if (transcriptAudioRef.current) {
          transcriptAudioRef.current.src = "";
        }
      } catch {
        /* ignore */
      }
      void (async () => {
        await recoverRecordingDraftOnOpen();
        await refreshVisitDocumentation({ showLoadingOverlay: true });
      })();
    }
  }, [isOpen, refreshVisitDocumentation, recoverRecordingDraftOnOpen]);

  const doneCount = useMemo(() => checklist.filter((c) => c.done).length, [checklist]);
  const totalCount = checklist.length;
  const progressPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  const patientTitle = useMemo(() => {
    if (!visitData) return "Patient";
    const emp = visitData.employee || visitData;
    const last = emp.last_name || emp.nameLast || "";
    const first = emp.first_name || emp.nameFirst || "";
    const type = visitData.visit_category || visitData.type || "";
    if (last || first) return `${last}, ${first} — ${type}`;
    return type || "Patient Visit";
  }, [visitData]);

  const patientMetaLine = useMemo(() => {
    if (!visitData) return "";
    const emp = visitData.employee || visitData;
    const dobRaw = emp.dob || emp.date_of_birth || "";
    const pp =
      visitData.primary_provider ||
      visitData.primaryProvider ||
      null;
    const providerCombined =
      pp &&
      `${pp.first_name || pp.nameFirst || ""} ${pp.last_name || pp.nameLast || ""}`.trim();
    const provider =
      providerCombined && providerCombined.length > 0
        ? `Dr. ${providerCombined}`
        : "";
    const parts = [];
    if (dobRaw) parts.push(`DOB ${formatDOB(dobRaw)}`);
    if (provider) parts.push(provider);
    return parts.join(" · ");
  }, [visitData]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Purpose: Tears down Web Audio nodes used for live waveform (before stopping media tracks).
   * Inputs/Output: none
   */
  const detachViz = useCallback(() => {
    setVizAnalyser(null);
    try {
      vizSourceRef.current?.disconnect();
    } catch {
      /* ignore */
    }
    vizSourceRef.current = null;
    const ctx = vizAudioContextRef.current;
    if (ctx && ctx.state !== "closed") {
      ctx.close().catch(() => {});
    }
    vizAudioContextRef.current = null;
  }, []);

  const detachPlaybackViz = useCallback(() => {
    setVizAnalyser(null);
    const analyser = previewPlaybackAnalyserRef.current;
    if (analyser) {
      try {
        analyser.disconnect();
      } catch {
        /* ignore */
      }
      previewPlaybackAnalyserRef.current = null;
    }
  }, []);

  /** Tears down preview Web Audio graph (required after re-record; MediaElementSource is one-per-element). */
  const releasePreviewAudioGraph = useCallback(() => {
    detachPlaybackViz();
    previewMediaElementSourceRef.current = null;
    const ctx = playbackVizContextRef.current;
    if (ctx && ctx.state !== "closed") {
      ctx.close().catch(() => {});
    }
    playbackVizContextRef.current = null;
    setAudioPreviewInstanceKey((k) => k + 1);
  }, [detachPlaybackViz]);

  const attachPreviewPlaybackAnalyser = useCallback(
    async (audioEl) => {
      if (!audioEl) return;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;

      detachViz();
      let ctx = playbackVizContextRef.current;
      if (!ctx || ctx.state === "closed") {
        ctx = new Ctx();
        playbackVizContextRef.current = ctx;
        previewMediaElementSourceRef.current = null;
      }
      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      detachPlaybackViz();

      if (!previewMediaElementSourceRef.current) {
        try {
          previewMediaElementSourceRef.current = ctx.createMediaElementSource(audioEl);
        } catch (e) {
          console.error(e);
          return;
        }
      }

      try {
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.72;
        previewPlaybackAnalyserRef.current = analyser;
        previewMediaElementSourceRef.current.connect(analyser);
        analyser.connect(ctx.destination);
        setVizAnalyser(analyser);
      } catch (e) {
        console.error(e);
      }
    },
    [detachPlaybackViz, detachViz]
  );

  const cleanupStream = useCallback(() => {
    detachViz();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, [detachViz]);

  const clearTranscriptStreamPlayback = useCallback(() => {
    try {
      transcriptAudioRef.current?.pause?.();
      if (transcriptAudioRef.current) transcriptAudioRef.current.src = "";
      setPlayingTranscriptSegmentId(null);
      setTranscriptPlayLoadingId(null);
      if (transcriptObjectUrlRef.current) {
        URL.revokeObjectURL(transcriptObjectUrlRef.current);
        transcriptObjectUrlRef.current = null;
      }
    } catch {
      /* ignore */
    }
  }, []);

  const resetRecordingHardware = useCallback(() => {
    try {
      audioPreviewRef.current?.pause?.();
      setIsPlayingPreview(false);
    } catch {
      /* ignore */
    }
    stopTimer();
    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        // Closing/cancelling an in-progress capture should not retain preview playback on reopen.
        suppressPreviewOnStopRef.current = true;
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    cleanupStream();
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    setRecordedBlob(null);
    recordedBlobRef.current = null;
    setElapsed(0);
    setFinalizingRecording(false);
    setPipelinePhase("idle");
    setRecordingPhase("idle");
  }, [stopTimer, cleanupStream]);

  /**
   * Purpose: Clears microphone pipeline only; preserves on-screen merged note and transcript (voice-first continuation).
   * Inputs/Output: none
   */
  const handleFullCleanup = useCallback(() => {
    resetRecordingHardware();
    setActiveTab("clinical");
  }, [resetRecordingHardware]);

  /**
   * Purpose: Clears mic state and closes modal; revokes preview URL and stops audio element.
   * Inputs/Output: none
   */
  const runClose = useCallback(() => {
    handleFullCleanup();
    try {
      audioPreviewRef.current?.pause?.();
      if (audioPreviewRef.current) audioPreviewRef.current.src = "";
      setIsPlayingPreview(false);
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
      clearTranscriptStreamPlayback();
    } catch {
      /* ignore */
    }
    onClose?.();
  }, [clearTranscriptStreamPlayback, handleFullCleanup, onClose]);

  /**
   * Purpose: Opens shared Alert confirm when closing would discard work; otherwise closes immediately.
   * Inputs/Output: none
   */
  const requestClose = useCallback(() => {
    const pipelineBusy = isSubmitting || pipelinePhase !== "idle";
    const recordBusy =
      recordingPhase === "recording" ||
      recordingPhase === "paused" ||
      isRecording ||
      isPaused ||
      finalizingRecording;
    if (recordBusy || pipelineBusy) {
      setCloseConfirmOpen(true);
      return;
    }
    runClose();
  }, [finalizingRecording, isPaused, isRecording, isSubmitting, pipelinePhase, runClose]);

  const confirmCloseModal = useCallback(() => {
    setCloseConfirmOpen(false);
    runClose();
  }, [runClose]);

  const handleCancel = requestClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const h = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [isOpen, requestClose]);

  useEffect(() => {
    return () => {
      stopTimer();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
      cleanupStream();
    };
  }, [stopTimer, cleanupStream]);

  const getMicrophoneStream = async () => {
    if (!navigator.mediaDevices) navigator.mediaDevices = {};
    if (!navigator.mediaDevices.getUserMedia) {
      const legacy =
        navigator.getUserMedia ||
        navigator.webkitGetUserMedia ||
        navigator.mozGetUserMedia ||
        navigator.msGetUserMedia;
      if (legacy) {
        navigator.mediaDevices.getUserMedia = (constraints) =>
          new Promise((resolve, reject) => legacy.call(navigator, constraints, resolve, reject));
      } else {
        throw new Error("Browser does not support audio recording.");
      }
    }
    const attempts = [
      { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } },
      { audio: true },
      { audio: { deviceId: "default" } },
    ];
    let lastError = null;
    for (const constraints of attempts) {
      try {
        return await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        lastError = err;
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") throw err;
        if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") throw err;
      }
    }
    throw lastError || new Error("Unable to access microphone.");
  };

  const clearDraftCaches = useCallback(async () => {
    if (!visitId) return;
    await deleteDraftFromIndexedDB(visitId);
    void VisitsService.deleteAudioDraft(visitId);
    setDraftBlob(null);
    setDraftLoadedFromCache(false);
    setDraftWaveformPeaks(null);
    previewDurationSecRef.current = 0;
  }, [visitId]);

  /**
   * @param {{ mode?: 'fresh'|'resume'|'recordMore' }} [options]
   */
  const handleStartOrResume = async (options = {}) => {
    const startMode = options.mode || "fresh";
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === "paused") {
      try {
        recorder.resume();
        setIsPaused(false);
        isRecordingRef.current = true;
        setIsRecording(true);
        setRecordingPhase("recording");
        timerRef.current = setInterval(() => {
          setElapsed((prev) => prev + 1);
        }, 1000);
      } catch (e) {
        console.error(e);
        Toast?.error?.("Could not resume recording.");
      }
      return;
    }

    if (isRecordingRef.current && recorder?.state === "recording") return;

    const resumeFromDraft =
      startMode === "resume" || recordingPhase === "paused" || draftLoadedFromCache;

    if (startMode === "recordMore") {
      segmentStartRef.current = Math.max(0, Math.floor(elapsedRef.current || elapsed));
      segmentLimitAutoPauseFiredRef.current = false;
      setSegmentLimitBanner(null);
      setGeneratePulseActive(false);
      audioChunksRef.current = [];
      setRecordedBlob(null);
      recordedBlobRef.current = null;
      setDraftBlob(null);
      setDraftLoadedFromCache(false);
      setDraftWaveformPeaks(null);
    } else if (!resumeFromDraft) {
      setRecordingSegments([]);
      segmentStartRef.current = 0;
      segmentLimitAutoPauseFiredRef.current = false;
      setSegmentLimitBanner(null);
      setGeneratePulseActive(false);
    }

    try {
      audioPreviewRef.current?.pause?.();
      setIsPlayingPreview(false);
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
      if (audioPreviewRef.current) {
        audioPreviewRef.current.src = "";
      }
      clearTranscriptStreamPlayback();
    } catch {
      /* ignore */
    }
    setPlaybackBlob(null);

    if (recordedBlob && !resumeFromDraft) {
      setRecordedBlob(null);
      recordedBlobRef.current = null;
      setElapsed(0);
      await new Promise((r) => setTimeout(r, 50));
    }

    try {
      setIsPaused(false);
      if (!resumeFromDraft) {
        setRecordedBlob(null);
        recordedBlobRef.current = null;
        setElapsed(0);
        audioChunksRef.current = [];
        setDraftBlob(null);
        setDraftLoadedFromCache(false);
      }
      setChunkVersion((v) => (resumeFromDraft ? v : 0));
      detachViz();

      const stream = await getMicrophoneStream();
      streamRef.current = stream;

      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        vizAudioContextRef.current = ctx;
        if (ctx.state === "suspended") {
          await ctx.resume();
        }
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.72;
        const src = ctx.createMediaStreamSource(stream);
        vizSourceRef.current = src;
        src.connect(analyser);
        setVizAnalyser(analyser);
      } catch (ve) {
        console.error(ve);
        setVizAnalyser(null);
      }

      if (typeof window.MediaRecorder === "undefined") {
        stream.getTracks().forEach((t) => t.stop());
        Toast?.error?.("Audio recording is not supported in this browser.");
        return;
      }

      // Cap Opus bitrate so a full 5-min segment stays small (~1.2 MB) and never trips PHP upload limits.
      const recorderOptions = { audioBitsPerSecond: 32000 };
      const preferredMimeTypes = ["audio/webm;codecs=opus", "audio/webm"];
      for (const mimeType of preferredMimeTypes) {
        if (typeof window.MediaRecorder.isTypeSupported === "function" && window.MediaRecorder.isTypeSupported(mimeType)) {
          recorderOptions.mimeType = mimeType;
          break;
        }
      }
      let recorder;
      try {
        recorder = new MediaRecorder(stream, recorderOptions);
      } catch (e) {
        console.error("MediaRecorder init with options failed; falling back to defaults.", e);
        recorder = new MediaRecorder(stream);
      }
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          setChunkVersion((v) => v + 1);
        }
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        cleanupStream();
        setIsPaused(false);
        setVizAnalyser(null);

        if (suppressPreviewOnStopRef.current) {
          suppressPreviewOnStopRef.current = false;
          setRecordedBlob(null);
          recordedBlobRef.current = null;
          setPlaybackBlob(null);
          setPreviewCurrentTimeSec(0);
          setFinalizingRecording(false);
          return;
        }

        void (async () => {
          try {
            const result = await trimSilenceFromRecordedBlob(audioBlob);
            setRecordedBlob(result.blob);
            recordedBlobRef.current = result.blob;
            setPlaybackBlob(result.blob);
            playbackBlobVisitIdRef.current = visitId;
            if (result.durationSec > 0) {
              setElapsed(Math.max(1, Math.ceil(result.durationSec)));
            }
          } catch {
            setRecordedBlob(audioBlob);
            recordedBlobRef.current = audioBlob;
            setPlaybackBlob(audioBlob);
            playbackBlobVisitIdRef.current = visitId;
          } finally {
            setFinalizingRecording(false);
          }
        })();
      };

      recorder.start(MEDIA_RECORDER_TIMESLICE_MS);
      isRecordingRef.current = true;
      setIsRecording(true);
      setRecordingPhase("recording");
      setPreviewCurrentTimeSec(0);
      timerRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      const name = error?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
        Toast?.error?.("Microphone permission denied. Please allow microphone access.");
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        Toast?.error?.("No microphone found. Please connect a microphone.");
      } else {
        Toast?.error?.(error?.message || "Unable to access microphone.");
      }
      isRecordingRef.current = false;
      setIsRecording(false);
      setRecordingPhase("idle");
      stopTimer();
      cleanupStream();
    }
  };

  const handlePauseRecording = async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;

    await waitForRecorderDataFlush(recorder);

    if (recorder.state === "recording") {
      recorder.pause();
    }
    setIsPaused(true);
    setRecordingPhase("paused");
    stopTimer();

    if (!segmentLimitAutoPauseFiredRef.current) {
      setSegmentLimitBanner(null);
    }

    const segEnd = Math.max(0, Math.floor(elapsedRef.current || elapsed));
    const segStart = Math.max(0, Math.floor(segmentStartRef.current));
    const segDur = Math.max(1, segEnd - segStart);
    setRecordingSegments((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        label: `Segment ${prev.length + 1}`,
        durationSec: segDur,
      },
    ]);
    segmentStartRef.current = segEnd;

    if (audioChunksRef.current.length > 0) {
      const pausedBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      const timerSec = Math.max(1, Math.round(elapsedRef.current || elapsed));
      setDraftBlob(pausedBlob);
      setPlaybackBlob(pausedBlob);
      playbackBlobVisitIdRef.current = visitId;
      void (async () => {
        try {
          const decoded = await getBlobDurationSec(pausedBlob);
          const durationSec = decoded > 0 ? Math.max(1, Math.ceil(decoded)) : timerSec;
          previewDurationSecRef.current = durationSec;
          setElapsed(durationSec);
          const peaks = await computeWaveformPeaksFromBlob(pausedBlob, 48);
          setDraftWaveformPeaks(peaks?.length ? peaks : null);
          await saveDraftToIndexedDB(visitId, pausedBlob, {
            durationSec,
            waveformPeaks: peaks,
          });
        } catch (e) {
          console.error(e);
          await saveDraftToIndexedDB(visitId, pausedBlob, { durationSec: timerSec });
        }
      })();
      uploadDraftToServerInBackground(pausedBlob);
    }
  };

  pauseRecordingRef.current = handlePauseRecording;

  /**
   * Purpose: Per-segment duration warning (4 min) and auto-pause at cap (5 min).
   * Side effects: sets banner state; calls handlePauseRecording at cap without stopping the session.
   */
  useEffect(() => {
    if (recordingPhase !== "recording" || isPaused) {
      return;
    }
    const segElapsed = Math.max(
      0,
      Math.floor(elapsed) - Math.floor(segmentStartRef.current)
    );

    if (
      segElapsed >= MAX_SEGMENT_DURATION_SEC &&
      !segmentLimitAutoPauseFiredRef.current
    ) {
      segmentLimitAutoPauseFiredRef.current = true;
      setSegmentLimitBanner("limit_reached");
      setGeneratePulseActive(true);
      void pauseRecordingRef.current();
      return;
    }

    if (segElapsed >= SEGMENT_WARNING_DURATION_SEC) {
      setSegmentLimitBanner((prev) => (prev === "limit_reached" ? prev : "warning"));
    } else if (segmentLimitBanner === "warning") {
      setSegmentLimitBanner(null);
    }
  }, [elapsed, recordingPhase, isPaused, segmentLimitBanner]);

  const waitForTrimmedBlob = async (maxMs = 4000) => {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      if (recordedBlobRef.current) return recordedBlobRef.current;
      if (audioChunksRef.current.length > 0) {
        const fallback = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (fallback.size > 0) return fallback;
      }
      await new Promise((r) => setTimeout(r, 80));
    }
    return recordedBlobRef.current;
  };

  /**
   * Purpose: Submits audio bytes through visit transcription queue, polls completion, runs merged AI note; optional recorder reset afterward.
   * Inputs: blobToSubmit (Blob|File); options.fileBaseName (string); options.resetRecorderAfter (boolean, default true)
   * Output: Promise<void>
   * Side effects: Loader, network, notes/transcript state, may reset mic UI
   */
  const runAudioPipelineFromBlob = useCallback(
    async (blobToSubmit, options = {}) => {
      const fileBaseName = options.fileBaseName || `transcription-${Date.now()}.wav`;
      const resetRecorderAfter = options.resetRecorderAfter !== false;

      if (!blobToSubmit || blobToSubmit.size === 0) {
        Toast?.error?.("No audio to submit.");
        return;
      }
      if (!visitId) {
        Toast?.error?.("Missing visit. Cannot save recording.");
        return;
      }

      const speechOk = await assertBlobHasAudibleSpeech(blobToSubmit, Toast);
      if (!speechOk) {
        setFinalizingRecording(false);
        return;
      }

      setPlaybackBlob(blobToSubmit);
      playbackBlobVisitIdRef.current = visitId;

      try {
        Loader?.(true);
        setIsSubmitting(true);
        const file = new File([blobToSubmit], fileBaseName, {
          type: blobToSubmit.type || "audio/wav",
        });
        const formData = new FormData();
        formData.append("file", file);
        formData.append("visit_id", visitId);
        if (profile?.user_id) {
          formData.append("created_by", profile.user_id);
        }
        const saved = await VisitsService.submitAudio(formData);
        const saveFailed =
          saved == null ||
          (typeof saved === "object" &&
            (saved.status === "ERROR" ||
              saved.failed === true ||
              (typeof saved.code === "number" && saved.code >= 400)));
        if (saveFailed) {
          let msg = "Could not save recording.";
          if (saved?.data != null) {
            if (typeof saved.data === "string") msg = saved.data;
            else if (typeof saved.data === "object") {
              const first = Object.values(saved.data)[0];
              if (Array.isArray(first) && first[0]) msg = String(first[0]);
            }
          }
          Toast?.error?.(msg);
          return;
        }

        const audioId = saved?.id;
        if (!audioId) {
          Toast?.error?.("Upload succeeded but no recording id was returned.");
          return;
        }

        setLatestUploadedAudioId(Number(audioId));
        setRecordingPhase("processing");
        void clearDraftCaches();
        Loader?.(false);
        setPipelinePhase("processing");
        Toast?.success?.(
          saved?.message || "Recording saved. Transcription and notes are processing in the background."
        );

        if (resetRecorderAfter) {
          resetRecordingHardware();
        }

        /**
         * Purpose: Polls `/audio/visit-voice-status` until this clip is transcribed and merged-note work finishes (or runs sync generate when auto-merge is off).
         * Inputs: targetAudioId (server audio_records id)
         * Output: Promise<void>
         */
        const pollVoicePipelineUntilSettled = async (targetAudioId) => {
          const timeoutMs = 180000;
          const start = Date.now();
          consecutiveNullCountRef.current = 0;

          const getPollInterval = (nullCount) => {
            if (nullCount >= 9) return 10000;
            if (nullCount >= 5) return 6000;
            return 3000;
          };

          // Give backend processing a moment to bootstrap before first status poll.
          await sleep(8000);

          while (Date.now() - start < timeoutMs) {
            if (voicePollAbortRef.current) {
              return;
            }

            const status = await VisitsService.getVisitVoiceProcessingStatus(visitId);
            if (status?.__authError) {
              voicePollAbortRef.current = true;
              throw new Error("Session expired while processing transcription. Please login again.");
            }
            if (!status) {
              consecutiveNullCountRef.current += 1;
              if (consecutiveNullCountRef.current >= 12) {
                voicePollAbortRef.current = true;
                Toast?.warning?.(
                  "Audio processing is taking longer than expected. Please refresh the page to check the status."
                );
                return;
              }
              await sleep(getPollInterval(consecutiveNullCountRef.current));
              continue;
            }
            consecutiveNullCountRef.current = 0;

            if (status.note_finalized) {
              setIsFinalizedNote(true);
              setEmrSyncDisplayStatus(resolveEmrSyncDisplayStatus(null, status));
            }

            if (status.transcription_busy) {
              setPipelinePhase("transcribing");
            } else if (status.merge_pending) {
              setPipelinePhase("generating");
            } else {
              setPipelinePhase("processing");
            }

            if (Array.isArray(status.transcript_segments)) {
              setTranscriptSegments(status.transcript_segments);
              const mergedTxtH = status.transcript_segments
                .map((s) => String(s.text || "").trim())
                .filter(Boolean)
                .join("\n\n---\n\n");
              setCombinedTranscript(mergedTxtH);
            }

            const row = (status.audio_records || []).find((r) => Number(r.id) === Number(targetAudioId));
            const st = (row?.status || "").toLowerCase();

            if (st === "failed") {
              throw new Error(row?.transcript_text || "Transcription failed for this recording.");
            }

            if (st === "completed") {
              if (!status.auto_merge_enabled) {
                const gen = await VisitsService.generateMergedClinicalNote(visitId, { forceOverwrite: true });
                if (gen.locked) {
                  const lockMsg =
                    gen.message ||
                    "Note is locked after manual edits; transcript saved but chart note was not overwritten.";
                  if (typeof Toast?.warning === "function") Toast.warning(lockMsg);
                  else Toast?.error?.(lockMsg);
                } else if (!gen.success) {
                  Toast?.error?.(gen.error || "Could not generate merged clinical note.");
                } else {
                  Toast?.success?.("Clinical notes updated.");
                }
                await refreshVisitDocumentation(
                  gen.success && gen.data?.id ? { mergedNoteDetail: gen.data } : {}
                );
                setActiveTab("clinical");
                return;
              }

              if (!status.merge_pending && !status.transcription_busy) {
                if (!status.merged_note_id) {
                  setPipelinePhase("generating");
                  const gen = await VisitsService.generateMergedClinicalNote(visitId, { forceOverwrite: true });
                  if (gen.locked) {
                    const lockMsg =
                      gen.message ||
                      "Note is locked after manual edits; transcript saved but chart note was not overwritten.";
                    if (typeof Toast?.warning === "function") Toast.warning(lockMsg);
                    else Toast?.error?.(lockMsg);
                  } else if (!gen.success) {
                    Toast?.error?.(gen.error || "Could not generate merged clinical note.");
                  } else {
                    Toast?.success?.("Clinical notes updated.");
                  }
                  await refreshVisitDocumentation(
                    gen.success && gen.data?.id ? { mergedNoteDetail: gen.data } : {}
                  );
                  setActiveTab("clinical");
                  return;
                }

                await refreshVisitDocumentation(
                  status.merged_note_id ? { mergedNoteIdHint: status.merged_note_id } : {}
                );
                if (status.note_locked) {
                  const w =
                    "Note is locked — new audio was transcribed; the chart note was not auto-updated.";
                  if (typeof Toast?.warning === "function") Toast.warning(w);
                  else Toast?.error?.(w);
                } else {
                  Toast?.success?.("Clinical notes updated.");
                }
                setActiveTab("clinical");
                return;
              }
            }

            await sleep(getPollInterval(consecutiveNullCountRef.current));
          }

          if (typeof Toast?.warning === "function") {
            Toast.warning("Processing is taking longer than expected. Refreshing saved data.");
          }
          await refreshVisitDocumentation();
        };

        try {
          await pollVoicePipelineUntilSettled(audioId);
        } catch (pollErr) {
          console.error(pollErr);
          const msg = String(pollErr?.message || "");
          const isAuthErr = /session expired|login again|unauth/i.test(msg);
          Toast?.error?.(msg || "Could not complete transcription pipeline.");
          if (!isAuthErr) {
            await refreshVisitDocumentation();
          }
        }
      } catch (err) {
        console.error(err);
        Toast?.error?.("Could not complete recording pipeline.");
      } finally {
        Loader?.(false);
        setIsSubmitting(false);
        setFinalizingRecording(false);
        setPipelinePhase("idle");
        setRecordingPhase((prev) => (prev === "processing" ? "idle" : prev));
      }
    },
    [visitId, profile, Toast, Loader, refreshVisitDocumentation, resetRecordingHardware, clearDraftCaches]
  );

  /**
   * Purpose: Builds the blob to submit on Accept / Chart from in-memory chunks and/or recovered draft.
   */
  const resolveBlobForAccept = useCallback(async () => {
    if (audioChunksRef.current.length > 0) {
      const fromChunks = new Blob(audioChunksRef.current, { type: "audio/webm" });
      if (fromChunks.size > 0) return fromChunks;
    }
    if (draftBlob && draftBlob.size > 0) return draftBlob;
    if (recordedBlobRef.current?.size) return recordedBlobRef.current;
    return waitForTrimmedBlob();
  }, [draftBlob]);

  /**
   * Purpose: Stops mic, submits the clip, runs merged transcription + AI note pipeline (only charting entry point).
   */
  const handleGenerateClinicalNote = async () => {
    if (isSubmitting || pipelinePhase !== "idle" || recordingPhase === "processing") return;

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      setFinalizingRecording(true);
    }
    stopTimer();
    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);
    suppressPreviewOnStopRef.current = false;

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    } else {
      cleanupStream();
      setFinalizingRecording(false);
    }

    const blobToSubmit = await resolveBlobForAccept();

    if (!blobToSubmit || blobToSubmit.size === 0) {
      Toast?.error?.("No audio recorded. Record or resume a paused take, then Generate Clinical Note.");
      setFinalizingRecording(false);
      return;
    }
    if (!visitId) {
      Toast?.error?.("Missing visit. Cannot save recording.");
      setFinalizingRecording(false);
      return;
    }

    setRecordingPhase("processing");
    await runAudioPipelineFromBlob(blobToSubmit, {
      fileBaseName: `transcription-${Date.now()}.wav`,
      resetRecorderAfter: true,
    });
  };

  const confirmRerecord = useCallback(async () => {
    setRerecordConfirmOpen(false);
    if (!visitId) return;

    Loader?.(true);
    try {
      const resetOk = await VisitsService.resetVisitVoiceDocumentation(visitId);
      if (!resetOk) {
        Toast?.error?.("Could not clear clinical note and transcripts.");
        return;
      }

      suppressPreviewOnStopRef.current = true;
      resetRecordingHardware();
      audioChunksRef.current = [];
      setChunkVersion(0);
      await clearDraftCaches();
      setRecordedBlob(null);
      recordedBlobRef.current = null;
      setPlaybackBlob(null);
      setDraftWaveformPeaks(null);
      previewDurationSecRef.current = 0;
      releasePreviewAudioGraph();
      setRecordingPhase("idle");
      setIsPaused(false);
      setElapsed(0);
      setNotesCollection([]);
      setNotesCollectionDraft(null);
      setClinicalSectionEditingId(null);
      setCombinedTranscript("");
      setTranscriptSegments([]);
      setMergedNoteId(null);
      setIsFinalizedNote(false);
      setEmrSyncDisplayStatus(null);
      setLatestUploadedAudioId(null);
      setRecordingSegments([]);
      segmentStartRef.current = 0;
      setChecklist(VISIT_DOC_CHECKLIST_SEED.map((c) => ({ ...c })));
      Toast?.success?.("Recording, clinical note, and transcripts cleared.");
    } catch (e) {
      console.error(e);
      Toast?.error?.("Could not reset documentation.");
    } finally {
      Loader?.(false);
    }
  }, [visitId, clearDraftCaches, resetRecordingHardware, releasePreviewAudioGraph, Toast, Loader]);

  /**
   * Purpose: Fetches merged visit chart note PDF blob and triggers browser download (after provider acknowledgment).
   * Inputs: none — uses mergedNoteId and visitId from state
   * Output: Promise<void>
   * Side effects: Network request, browser download, Loader / Toast
   */
  const performMergedNotePdfDownload = useCallback(async () => {
    if (!mergedNoteId) {
      Toast?.error?.("No chart note available to download yet.");
      return;
    }
    Loader?.(true);
    try {
      const res = await httpInstance.get(`audio/notes/${mergedNoteId}/download`, {
        responseType: "blob",
      });
      if (res.status !== 200 || !(res.data instanceof Blob)) {
        Toast?.error?.("Could not download chart note.");
        return;
      }
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chart-note-visit-${visitId}-${mergedNoteId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      Toast?.success?.("Download started.");
    } catch (e) {
      console.error(e);
      Toast?.error?.("Could not download chart note.");
    } finally {
      Loader?.(false);
    }
  }, [mergedNoteId, visitId, Toast, Loader]);

  /**
   * Purpose: Opens AI documentation acknowledgment dialog; download runs only after checkbox + confirm.
   * Inputs: none
   * Output: void
   * Side effects: Sets modal open and resets checkbox
   */
  const openMergedNoteDownloadAck = useCallback(() => {
    if (!mergedNoteId) {
      Toast?.error?.("No chart note available to download yet.");
      return;
    }
    setAiAckPurpose("download");
    setAiDownloadAckChecked(false);
    setAiDownloadAckOpen(true);
  }, [mergedNoteId, Toast]);

  const openFinalizeChartNoteAck = useCallback(() => {
    if (!mergedNoteId) {
      Toast?.error?.("No chart note available to finalize yet.");
      return;
    }
    if (isFinalizedNote) {
      return;
    }
    setAiAckPurpose("finalize");
    setAiDownloadAckChecked(false);
    setAiDownloadAckOpen(true);
  }, [mergedNoteId, isFinalizedNote, Toast]);

  /**
   * Purpose: Closes AI download acknowledgment without downloading.
   * Inputs: none
   * Output: void
   * Side effects: Clears modal and checkbox state
   */
  const closeMergedNoteDownloadAck = useCallback(() => {
    setAiDownloadAckOpen(false);
    setAiDownloadAckChecked(false);
  }, []);

  useEffect(() => {
    if (!aiDownloadAckOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMergedNoteDownloadAck();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aiDownloadAckOpen, closeMergedNoteDownloadAck]);

  /**
   * Purpose: Closes acknowledgment dialog and starts the PDF download when the provider has checked the box.
   * Inputs: none
   * Output: Promise<void>
   * Side effects: Closes dialog; invokes performMergedNotePdfDownload
   */
  const confirmMergedNoteDownloadAck = useCallback(async () => {
    if (!aiDownloadAckChecked) {
      return;
    }
    setAiDownloadAckOpen(false);
    setAiDownloadAckChecked(false);
    await performMergedNotePdfDownload();
  }, [aiDownloadAckChecked, performMergedNotePdfDownload]);

  const confirmFinalizeChartNoteAck = useCallback(async () => {
    if (!aiDownloadAckChecked || !mergedNoteId || !visitId || finalizeNoteBusy) {
      return;
    }
    setAiDownloadAckOpen(false);
    setAiDownloadAckChecked(false);
    setFinalizeNoteBusy(true);
    try {
      const result = await VisitsService.finalizeAiNote(mergedNoteId, visitId, true, "finalize");
      const note = result?.note ?? result;
      if (!result?.success && !note?.id) {
        Toast?.error?.("Could not finalize chart note.");
        return;
      }
      setIsFinalizedNote(true);
      if (note?.id) {
        setMergedNoteId(note.id);
        if (note.notes_collection?.length) {
          setNotesCollection(note.notes_collection);
          setChecklist(
            buildChecklistFromNotesAndTranscripts(note.notes_collection, transcriptSegments)
          );
        }
      }
      setEmrSyncDisplayStatus(
        resolveEmrSyncDisplayStatus(note, {
          finalize_mapping_pending: result?.finalize_mapping_pending,
        }) ?? "pending"
      );

      const emrMap = result?.emr_map;
      if (emrMap?.mode === "queued" || result?.finalize_mapping_pending) {
        Toast?.success?.(
          emrMap?.message ||
            "Chart note finalized. Visit mapping is processing in the background."
        );
      } else {
        Toast?.success?.(
          emrMap?.message || "Chart note finalized. Recording is now locked."
        );
      }
    } catch (e) {
      console.error(e);
      Toast?.error?.("Could not finalize chart note.");
    } finally {
      setFinalizeNoteBusy(false);
    }
  }, [
    aiDownloadAckChecked,
    mergedNoteId,
    visitId,
    finalizeNoteBusy,
    transcriptSegments,
    Toast,
  ]);

  /**
   * Purpose: When a completed checklist row is activated, highlights it and smooth-scrolls the Clinical note panel to the matching section.
   * Inputs: id (string) — static checklist id (cc, hx, exam, …)
   * Output: void
   * Side effects: Updates checklist `active`, switches to clinical tab, scrolls `.snap-panel` ancestor, focuses the section wrapper
   */
  const handleChecklistClick = useCallback(
    (id) => {
      const hit = checklist.find((item) => item.id === id);
      if (!hit?.done) return;

      setChecklist((prev) =>
        prev.map((item) => ({
          ...item,
          active: item.id === id,
        }))
      );

      setActiveTab("clinical");

      const sectionId = `${SNAP_CLINICAL_SECTION_ID_PREFIX}-${id}`;
      const runScroll = (attempt = 0) => {
        if (!document.getElementById(sectionId)) {
          if (attempt < 15) {
            window.setTimeout(() => runScroll(attempt + 1), 60);
          }
          return;
        }
        scrollSnapClinicalSectionIntoView(sectionId);
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(() => runScroll(0));
      });
    },
    [checklist]
  );

  /**
   * Purpose: Begins inline edit for one static checklist section (deep-clones full `notes_collection` for the draft).
   * Inputs: staticId (cc|hx|exam|…)
   * Output: void
   * Side effects: Sets draft array and `clinicalSectionEditingId`
   */
  const handleStartEditClinicalSection = useCallback(
    (staticId) => {
      if (
        !mergedNoteId ||
        isFinalizedNote ||
        isSubmitting ||
        pipelinePhase !== "idle" ||
        finalizingRecording
      ) {
        return;
      }
      setNotesCollectionDraft(JSON.parse(JSON.stringify(notesCollection)));
      setClinicalSectionEditingId(staticId);
    },
    [mergedNoteId, isFinalizedNote, isSubmitting, pipelinePhase, finalizingRecording, notesCollection]
  );

  /**
   * Purpose: Discards clinical section draft without saving.
   * Inputs: none
   * Output: void
   */
  const handleCancelEditClinicalSection = useCallback(() => {
    setClinicalSectionEditingId(null);
    setNotesCollectionDraft(null);
  }, []);

  /**
   * Purpose: Updates one label’s HTML in the draft from plain textarea content.
   * Inputs: notesIndex (number), labelIndex (number), plainText (string)
   * Output: void
   */
  const handleDraftFieldPlainTextChange = useCallback((notesIndex, labelIndex, plainText) => {
    setNotesCollectionDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      if (next[notesIndex]?.label?.[labelIndex]) {
        next[notesIndex].label[labelIndex].value = editableTextToClinicalHtml(plainText);
      }
      return next;
    });
  }, []);

  /**
   * Purpose: Persists section draft without merge lock or section preservation — a later transcript merge can overwrite the full note.
   * Inputs: none (saves current `clinicalSectionEditingId` bucket via full draft)
   * Output: Promise<void>
   * Side effects: API call, Toast, updates notes_collection / checklist
   */
  const handleSaveClinicalSection = useCallback(
    async () => {
      if (!mergedNoteId || !visitId || !notesCollectionDraft) return;
      setClinicalSectionSaveBusy(true);
      Loader?.(true);
      try {
        const data = await VisitsService.updateAiNote(mergedNoteId, visitId, notesCollectionDraft, {
          userEdited: false,
          editedSectionKeys: [],
          releaseVoiceMergeLock: true,
        });
        if (isFailedVoiceServiceResult(data)) {
          Toast?.error?.("Could not save clinical note.");
          return;
        }
        const next = Array.isArray(data?.notes_collection) ? data.notes_collection : notesCollectionDraft;
        setNotesCollection(next);
        setChecklist(buildChecklistFromNotesAndTranscripts(next, transcriptSegments));
        setClinicalSectionEditingId(null);
        setNotesCollectionDraft(null);
        Toast?.success?.("Section saved.");
      } catch (e) {
        console.error(e);
        Toast?.error?.("Could not save clinical note.");
      } finally {
        setClinicalSectionSaveBusy(false);
        Loader?.(false);
      }
    },
    [mergedNoteId, visitId, notesCollectionDraft, transcriptSegments, Toast, Loader]
  );

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  /**
   * Purpose: Builds a Blob for local preview (final take, retained clip, or in-progress WebM chunks).
   * Inputs: none (reads refs/state)
   * Output: Promise<Blob|null>
   */
  const buildPreviewBlob = useCallback(async () => {
    if (recordedBlob && recordedBlob.size > 0) return recordedBlob;
    if (playbackBlob && playbackBlob.size > 0) return playbackBlob;
    const rec = mediaRecorderRef.current;
    if (rec && (rec.state === "recording" || rec.state === "paused")) {
      try {
        if (typeof rec.requestData === "function") {
          rec.requestData();
        }
      } catch {
        /* ignore */
      }
      await new Promise((r) => setTimeout(r, 150));
      const raw = new Blob(audioChunksRef.current, { type: "audio/webm" });
      if (raw.size > 0) return raw;
    }
    return null;
  }, [recordedBlob, playbackBlob]);

  /**
   * Purpose: Plays or stops server-backed audio for a transcript segment (`GET audio/{id}/stream`).
   * Inputs: segmentId — audio_records id shown on segment
   * Side effects: fetches blob, object URL, transcript <audio>; pauses recording preview
   */
  const toggleTranscriptSegmentPlayback = useCallback(
    async (segmentId) => {
      const el = transcriptAudioRef.current;
      if (!segmentId || !el) return;

      audioPreviewRef.current?.pause?.();
      setIsPlayingPreview(false);

      const same =
        playingTranscriptSegmentId != null &&
        Number(playingTranscriptSegmentId) === Number(segmentId);
      if (same && !el.paused) {
        el.pause();
        setPlayingTranscriptSegmentId(null);
        if (transcriptObjectUrlRef.current) {
          URL.revokeObjectURL(transcriptObjectUrlRef.current);
          transcriptObjectUrlRef.current = null;
        }
        el.src = "";
        el.onended = null;
        return;
      }

      try {
        el.pause();
        if (transcriptObjectUrlRef.current) {
          URL.revokeObjectURL(transcriptObjectUrlRef.current);
          transcriptObjectUrlRef.current = null;
        }
        el.src = "";
        el.onended = null;

        setTranscriptPlayLoadingId(segmentId);
        const blob = await VisitsService.fetchAudioStreamBlob(segmentId);
        if (!blob || blob.size === 0) {
          Toast?.error?.("Audio not available for this recording.");
          return;
        }

        const url = URL.createObjectURL(blob);
        transcriptObjectUrlRef.current = url;
        el.src = url;
        el.onended = () => {
          setPlayingTranscriptSegmentId(null);
          if (transcriptObjectUrlRef.current) {
            URL.revokeObjectURL(transcriptObjectUrlRef.current);
            transcriptObjectUrlRef.current = null;
          }
          el.src = "";
          el.onended = null;
        };
        await el.play();
        setPlayingTranscriptSegmentId(segmentId);
      } catch (e) {
        console.error(e);
        Toast?.error?.("Could not play recording.");
        setPlayingTranscriptSegmentId(null);
        if (transcriptObjectUrlRef.current) {
          URL.revokeObjectURL(transcriptObjectUrlRef.current);
          transcriptObjectUrlRef.current = null;
        }
        el.src = "";
      } finally {
        setTranscriptPlayLoadingId(null);
      }
    },
    [Toast, playingTranscriptSegmentId]
  );

  /**
   * Purpose: Plays or stops hidden audio preview for the current in-memory recording.
   * Inputs: none
   * Output: none
   * Side effects: object URLs, <audio> play/pause
   */
  const togglePlayback = useCallback(async () => {
    const el = audioPreviewRef.current;
    if (!el) return;
    if (isPlayingPreview) {
      el.pause();
      setPreviewCurrentTimeSec(0);
      setIsPlayingPreview(false);
      detachPlaybackViz();
      return;
    }
    clearTranscriptStreamPlayback();
    const blob = await buildPreviewBlob();
    if (!blob || blob.size === 0) {
      Toast?.error?.("Nothing to play yet.");
      return;
    }
    let peaksForPlay = draftWaveformPeaks;
    if (!peaksForPlay?.length) {
      try {
        peaksForPlay = await computeWaveformPeaksFromBlob(blob, 48);
        if (peaksForPlay?.length) {
          setDraftWaveformPeaks(peaksForPlay);
        }
      } catch (e) {
        console.error(e);
      }
    }
    try {
      el.pause();
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
      detachPlaybackViz();
      const url = URL.createObjectURL(blob);
      previewObjectUrlRef.current = url;
      el.src = url;
      el.ontimeupdate = () => {
        setPreviewCurrentTimeSec(el.currentTime || 0);
      };
      el.onloadedmetadata = () => {
        if (Number.isFinite(el.duration) && el.duration > 0) {
          previewDurationSecRef.current = Math.ceil(el.duration);
        }
      };
      el.onended = () => {
        setIsPlayingPreview(false);
        setPreviewCurrentTimeSec(0);
        detachPlaybackViz();
        if (previewObjectUrlRef.current) {
          URL.revokeObjectURL(previewObjectUrlRef.current);
          previewObjectUrlRef.current = null;
        }
        el.src = "";
        el.onloadedmetadata = null;
        el.ontimeupdate = null;
        el.onended = null;
      };
      await el.play();
      if (Number.isFinite(el.duration) && el.duration > 0) {
        previewDurationSecRef.current = Math.ceil(el.duration);
      } else if (!previewDurationSecRef.current) {
        previewDurationSecRef.current = Math.max(1, Math.round(elapsedRef.current || elapsed));
      }
      await attachPreviewPlaybackAnalyser(el);
      setIsPlayingPreview(true);
    } catch (e) {
      console.error(e);
      Toast?.error?.("Could not play recording.");
      detachPlaybackViz();
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
    }
  }, [
    attachPreviewPlaybackAnalyser,
    buildPreviewBlob,
    clearTranscriptStreamPlayback,
    detachPlaybackViz,
    draftWaveformPeaks,
    elapsed,
    isPlayingPreview,
    Toast,
  ]);

  useEffect(() => {
    if (playingTranscriptSegmentId == null) return;
    const exists = transcriptSegments.some(
      (s) => Number(s.id) === Number(playingTranscriptSegmentId)
    );
    if (!exists) clearTranscriptStreamPlayback();
  }, [clearTranscriptStreamPlayback, playingTranscriptSegmentId, transcriptSegments]);

  if (!isOpen) return null;

  const pipelineBusy = isSubmitting || pipelinePhase !== "idle" || recordingPhase === "processing";
  const displayNotesCollection = notesCollectionDraft ?? notesCollection;
  const hasNoteContent =
    (Array.isArray(displayNotesCollection) && displayNotesCollection.length > 0) ||
    (Array.isArray(notesCollection) && notesCollection.length > 0);

  const workflowState = deriveWorkflowState({
    isFinalizedNote,
    isSubmitting,
    pipelinePhase,
    recordingPhase,
    isRecording,
    isPaused,
    mergedNoteId,
    hasNoteContent,
  });

  const totalRecordedSec = recordingSegments.reduce((sum, s) => sum + (s.durationSec || 0), 0);
  const rerecordSegmentCount = Math.max(
    recordingSegments.length,
    transcriptSegments.length,
    totalRecordedSec > 0 || draftBlob || recordedBlob || chunkVersion > 0 ? 1 : 0
  );
  const footerHint = WORKFLOW_FOOTER_HINTS[workflowState] || "";
  const finalizedEmrBanner =
    workflowState === "finalized" && emrSyncDisplayStatus
      ? EMR_SYNC_BANNER_BY_STATUS[emrSyncDisplayStatus] ?? EMR_SYNC_BANNER_BY_STATUS.synced
      : null;
  const clinicalSectionEditStartBlockReason = getClinicalSectionEditStartBlockReason({
    mergedNoteId,
    pipelineBusy,
    finalizingRecording,
    isFinalizedNote,
  });
  const waveReactive =
    (workflowState === "recording" && !!vizAnalyser && !isPaused) ||
    (workflowState === "paused" && isPlayingPreview && !!vizAnalyser);
  const showStaticDraftWave =
    workflowState === "paused" &&
    draftWaveformPeaks?.length > 0 &&
    (!isPlayingPreview || !vizAnalyser);
  const draftDurationSec = Math.max(
    1,
    Math.round(
      previewDurationSecRef.current ||
        (workflowState === "paused" ? totalRecordedSec : 0) ||
        elapsed ||
        0
    )
  );
  const wavePlaybackProgress = isPlayingPreview
    ? draftDurationSec > 0
      ? Math.min(1, (previewCurrentTimeSec || 0) / draftDurationSec)
      : 0
    : showStaticDraftWave
      ? 1
      : 0;
  const displayElapsed =
    workflowState === "idle"
      ? 0
      : isPlayingPreview
        ? Math.floor(previewCurrentTimeSec)
        : workflowState === "paused" && totalRecordedSec > 0
          ? totalRecordedSec
          : workflowState === "generated" || workflowState === "finalized"
            ? Math.max(totalRecordedSec, Math.floor(elapsed))
            : Math.max(0, Math.floor(elapsed));

  const showMainWave = workflowState === "recording" || workflowState === "paused";
  const showMiniWave = workflowState === "recording";
  const canPlayback =
    workflowState === "paused" &&
    ((recordedBlob && recordedBlob.size > 0) ||
      (playbackBlob && playbackBlob.size > 0) ||
      (draftBlob && draftBlob.size > 0));

  const recordCardClass = [
    "snap-record",
    `snap-record--${workflowState}`,
    workflowState === "paused" && "paused",
    workflowState === "idle" && "idle",
    workflowState === "generating" && "processing",
  ]
    .filter(Boolean)
    .join(" ");

  const statusLabel =
    workflowState === "recording"
      ? "LIVE RECORDING"
      : workflowState === "paused"
        ? "PAUSED"
        : workflowState === "generating"
          ? "PROCESSING"
          : workflowState === "generated"
            ? "NOTE READY"
            : workflowState === "finalized"
              ? "FINALIZED"
              : "READY";

  const hasRecordedContent =
    recordingSegments.length > 0 ||
    transcriptSegments.length > 0 ||
    draftLoadedFromCache ||
    (draftBlob && draftBlob.size > 0) ||
    (recordedBlob && recordedBlob.size > 0) ||
    chunkVersion > 0 ||
    !!mergedNoteId;

  const generateEnabled =
    workflowState === "paused" &&
    (audioChunksRef.current.length > 0 ||
      (draftBlob && draftBlob.size > 0) ||
      (recordedBlob && recordedBlob.size > 0) ||
      chunkVersion > 0);

  const rerecordEnabled =
    (workflowState === "paused" || workflowState === "generated") && hasRecordedContent;

  /**
   * Purpose: Renders checklist rows (icon + label + done badge) for sidebar and mobile checklist tab.
   * Inputs: none (reads checklist, handleChecklistClick from component scope)
   * Output: React element array
   */
  const renderChecklistRows = () =>
    checklist.map((item) => (
      <div
        key={item.id}
        className={`snap-cheat ${item.done ? "done" : ""} ${item.active ? "active" : ""}`}
        onClick={() => handleChecklistClick(item.id)}
        role="button"
        tabIndex={item.done ? 0 : -1}
        aria-disabled={!item.done}
        aria-label={
          item.done
            ? `${item.label} — complete, jump to section in clinical note`
            : `${item.label} — not yet documented in the note; open clinical note when this section appears`
        }
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleChecklistClick(item.id);
          }
        }}
      >
        <div className="snap-cheat-icon" style={{ background: SECTION_COLORS[item.id] }}>
          {SECTION_ICONS[item.id]}
          {item.done ? <span className="snap-cheat-check">✓</span> : null}
        </div>
        <div className="snap-cheat-label">{item.label}</div>
      </div>
    ));

  return (
    <div className="sh-snap-recording-modal snap-root snap-overlay" role="dialog" aria-modal="true">
      <div className="snap-modal" aria-busy={visitDocumentationLoading}>
        {visitDocumentationLoading ? (
          <div className="snap-visit-doc-loading-overlay" role="status" aria-live="polite">
            <div className="snap-visit-doc-loading-inner">
              <div className="snap-visit-doc-loading-spinner" aria-hidden />
              <p className="snap-visit-doc-loading-text">Loading visit documentation…</p>
              <span className="snap-visit-doc-loading-sr">Loading audio list, notes, and clinical summary.</span>
            </div>
          </div>
        ) : null}
        {/* Desktop sidebar — hidden on mobile */}
        <aside className="snap-sidebar snap-sidebar-desktop">
          <div className="snap-sidebar-header">
            <div className="snap-sidebar-title">Documentation</div>
            <div className="snap-sidebar-subtitle">Visit checklist</div>
          </div>
          <div className="snap-progress-card">
            <div className="snap-progress-row">
              <span className="snap-progress-label">Progress</span>
              <span className="snap-progress-count">
                {doneCount} of {totalCount} complete
              </span>
            </div>
            <div className="snap-progress-track">
              <div className="snap-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
          {renderChecklistRows()}
        </aside>

        <section className="snap-main">
          <div className="snap-main-header">
            <div className="snap-main-header-left">
              <div className="snap-kicker">Live visit recording</div>
              <div className="snap-patient-name">{patientTitle}</div>
              <div className="snap-patient-meta">{patientMetaLine}</div>
            </div>
            <button type="button" className="snap-close" onClick={handleCancel} aria-label="Close">
              ×
            </button>
          </div>

          <div className={recordCardClass}>
            <div className="snap-record-status">
              <div className="snap-record-status-left">
                {workflowState === "recording" ? (
                  <span className="snap-live-dot snap-live-dot--pulse" aria-hidden />
                ) : null}
                <span
                  className={`snap-record-label ${
                    workflowState === "recording" ? "snap-record-label--live" : ""
                  }`}
                >
                  {statusLabel}
                </span>
                {showMiniWave ? (
                  <VoiceWaveform
                    barCount={8}
                    small
                    reactive={waveReactive}
                    analyser={vizAnalyser}
                    staticPeaks={null}
                    playbackProgress={wavePlaybackProgress}
                  />
                ) : null}
              </div>
              <div className="snap-record-status-right">
                {workflowState === "paused" ? (
                  <button
                    type="button"
                    className="snap-record-play-pill"
                    onClick={() => void togglePlayback()}
                    disabled={!canPlayback}
                    aria-label={isPlayingPreview ? "Stop playback" : "Play recording"}
                    title={isPlayingPreview ? "Stop playback" : "Play recording"}
                  >
                    <IconPlayGlyph playing={isPlayingPreview} />
                    <span>{isPlayingPreview ? "Stop" : "Play"}</span>
                  </button>
                ) : null}
                <div className="snap-timer">{formatTime(displayElapsed)}</div>
              </div>
            </div>

            {segmentLimitBanner === "warning" && workflowState === "recording" ? (
              <div
                className="snap-segment-limit-banner snap-segment-limit-banner--warning"
                role="status"
                aria-live="polite"
              >
                ⚠ 1 minute remaining — recording will auto-pause at 5 minutes.
              </div>
            ) : null}
            {segmentLimitBanner === "limit_reached" && workflowState === "paused" ? (
              <div
                className="snap-segment-limit-banner snap-segment-limit-banner--limit"
                role="status"
                aria-live="polite"
              >
                Recording paused — 5 minute limit reached. Generate your clinical note now, or tap
                Record More to continue.
              </div>
            ) : null}

            {workflowState === "paused" && recordingSegments.length > 0 ? (
              <div className="snap-segment-badges" aria-label="Recorded segments">
                {recordingSegments.map((seg) => (
                  <span key={seg.id} className="snap-segment-badge">
                    {seg.label} · {formatTime(seg.durationSec)}
                  </span>
                ))}
                {totalRecordedSec > 0 ? (
                  <span className="snap-segment-total">Total {formatTime(totalRecordedSec)}</span>
                ) : null}
              </div>
            ) : null}

            {showMainWave ? (
              <div className="snap-record-wave-actions">
                <div className="snap-wave-grow">
                  <VoiceWaveform
                    barCount={48}
                    reactive={waveReactive}
                    analyser={vizAnalyser}
                    staticPeaks={showStaticDraftWave ? draftWaveformPeaks : null}
                    playbackProgress={wavePlaybackProgress}
                  />
                </div>
              </div>
            ) : null}

            <audio
              key={audioPreviewInstanceKey}
              ref={audioPreviewRef}
              preload="none"
              className="snap-audio-hidden"
              title="Recording preview"
            />
            <audio
              ref={transcriptAudioRef}
              preload="none"
              className="snap-audio-hidden"
              title="Saved recording playback"
            />
          </div>

          {/* Mobile-only: progress bar + 3-tab strip (Checklist / Clinical note / Transcript) */}
          <div className="snap-mobile-tabs-section">
            <div className="snap-mobile-progress">
              <span className="snap-progress-label">Progress</span>
              <span className="snap-progress-count">
                {doneCount} / {totalCount}
              </span>
              <div className="snap-progress-track snap-mobile-progress-track">
                <div className="snap-progress-fill" style={{ width: `${progressPct}%` }} />
              </div>
            </div>

            <div className="snap-mobile-tabs-with-download">
            <div className="snap-tabs snap-mobile-tabs" role="tablist">
              <button
                type="button"
                className={`snap-tab ${activeTab === "checklist" ? "active" : ""}`}
                onClick={() => handleTabClick("checklist")}
                role="tab"
                aria-selected={activeTab === "checklist"}
              >
                Checklist
              </button>
              <button
                type="button"
                className={`snap-tab ${activeTab === "clinical" ? "active" : ""}`}
                onClick={() => handleTabClick("clinical")}
                role="tab"
                aria-selected={activeTab === "clinical"}
              >
                Clinical note
              </button>
              <button
                type="button"
                className={`snap-tab ${activeTab === "transcript" ? "active" : ""}`}
                onClick={() => handleTabClick("transcript")}
                role="tab"
                aria-selected={activeTab === "transcript"}
              >
                Transcript
              </button>
            </div>
            <button
              type="button"
              className="snap-chart-download-icon snap-chart-download-icon-mobile"
              onClick={() => openMergedNoteDownloadAck()}
              disabled={!mergedNoteId || pipelineBusy}
              aria-label="Download chart note PDF"
              title={mergedNoteId ? "Download chart note PDF" : "Generate a note first"}
            >
              <IconDownloadGlyph />
            </button>
            </div>
          </div>

          {/* Desktop-only: 2-tab strip (Clinical note / Transcript) + download icon */}
          <div className="snap-tabs-row snap-desktop-tabs">
            <div className="snap-tabs" role="tablist">
              <button
                type="button"
                className={`snap-tab ${activeTab === "clinical" ? "active" : ""}`}
                onClick={() => handleTabClick("clinical")}
                role="tab"
                aria-selected={activeTab === "clinical"}
              >
                Clinical note
              </button>
              <button
                type="button"
                className={`snap-tab ${activeTab === "transcript" ? "active" : ""}`}
                onClick={() => handleTabClick("transcript")}
                role="tab"
                aria-selected={activeTab === "transcript"}
              >
                Transcript
              </button>
            </div>
            <button
              type="button"
              className="snap-chart-download-icon"
              onClick={() => openMergedNoteDownloadAck()}
              disabled={!mergedNoteId || pipelineBusy}
              aria-label="Download chart note PDF"
              title={mergedNoteId ? "Download chart note PDF" : "Generate a note first"}
            >
              <IconDownloadGlyph />
            </button>
          </div>

          {/* Checklist tab panel (mobile only, desktop uses sidebar) */}
          {activeTab === "checklist" ? (
            <div className="snap-panel active snap-checklist-panel" role="tabpanel">
              {renderChecklistRows()}
            </div>
          ) : null}

          {activeTab === "clinical" ? (
            <div className="snap-panel active" role="tabpanel">
              {finalizedEmrBanner ? (
                <div
                  className={`snap-finalized-emr-banner snap-finalized-emr-banner--${finalizedEmrBanner.modifier}`}
                  role="status"
                >
                  {finalizedEmrBanner.label}
                </div>
              ) : null}
              <StaticVisitNotePanel
                notesCollection={displayNotesCollection}
                editingStaticId={isFinalizedNote ? null : clinicalSectionEditingId}
                sectionEditStartBlockReason={clinicalSectionEditStartBlockReason}
                saveBusy={clinicalSectionSaveBusy}
                onStartEditSection={handleStartEditClinicalSection}
                onCancelEditSection={handleCancelEditClinicalSection}
                onSaveSection={(id) => void handleSaveClinicalSection(id)}
                onDraftFieldPlainTextChange={handleDraftFieldPlainTextChange}
              />
            </div>
          ) : null}

          {activeTab === "transcript" ? (
            <div className="snap-panel active snap-transcript-panel" role="tabpanel">
              {!transcriptSegments.length ? (
                <div className="snap-transcript-empty">
                  {workflowState === "generating"
                    ? "Updating transcripts…"
                    : "Completed recordings for this visit appear below (newest highlighted). Use Generate Clinical Note to add another."}
                </div>
              ) : (
                <div className="snap-transcript-segments">
                  {transcriptSegments.map((seg) => {
                    const isLatest = Number(seg.id) === Number(latestUploadedAudioId);
                    return (
                      <div
                        key={seg.id}
                        className={`snap-transcript-segment ${isLatest ? "snap-transcript-segment-latest" : ""}`}
                      >
                        <div className="snap-transcript-segment-meta">
                          <button
                            type="button"
                            className="snap-transcript-play-btn"
                            onClick={() => void toggleTranscriptSegmentPlayback(seg.id)}
                            disabled={transcriptPlayLoadingId === seg.id || pipelineBusy}
                            aria-busy={transcriptPlayLoadingId === seg.id}
                            aria-label={
                              playingTranscriptSegmentId === seg.id
                                ? "Stop audio for this transcript"
                                : "Play audio for this transcript"
                            }
                            title={playingTranscriptSegmentId === seg.id ? "Stop" : "Play recording"}
                          >
                            <IconPlayGlyph playing={playingTranscriptSegmentId === seg.id} />
                          </button>
                          <span className="snap-transcript-segment-id">Recording #{seg.id}</span>
                          {seg.created_at ? (
                            <span className="snap-transcript-segment-time">
                              {new Date(seg.created_at).toLocaleString()}
                            </span>
                          ) : null}
                          {seg.file_name ? (
                            <span className="snap-transcript-segment-file">{seg.file_name}</span>
                          ) : null}
                          {isLatest ? <span className="snap-transcript-segment-badge">Latest</span> : null}
                        </div>
                        <pre className="snap-transcript-segment-text">{String(seg.text || "").trim()}</pre>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          <div className="snap-footer">
            {workflowState === "generating" ? (
              <div className="snap-footer-generating" role="status" aria-live="polite">
                <div className="snap-footer-generating-spinner" aria-hidden />
                <span>Transcribing and generating note…</span>
              </div>
            ) : workflowState === "finalized" ? (
              <div className="snap-footer-finalized-only" role="status">
                {finalizedEmrBanner ? (
                  <div
                    className={`snap-finalized-emr-banner snap-finalized-emr-banner--footer snap-finalized-emr-banner--${finalizedEmrBanner.modifier}`}
                  >
                    {finalizedEmrBanner.label}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="snap-footer-actions">
                {workflowState === "idle" ? (
                  <button
                    type="button"
                    className="snap-btn snap-btn-start-full"
                    onClick={() => void handleStartOrResume({ mode: "fresh" })}
                    disabled={finalizingRecording}
                  >
                    Start Recording
                  </button>
                ) : null}

                {workflowState === "recording" ? (
                  <button
                    type="button"
                    className="snap-btn snap-btn-pause-full"
                    onClick={handlePauseRecording}
                    disabled={finalizingRecording}
                  >
                    <span className="snap-btn-icon">⏸</span>
                    Pause
                  </button>
                ) : null}

                {workflowState === "paused" ? (
                  <div className="snap-footer-paused-row">
                    <button
                      type="button"
                      className="snap-btn snap-btn-resume-secondary"
                      onClick={() => void handleStartOrResume({ mode: "resume" })}
                      disabled={finalizingRecording}
                    >
                      Resume Recording
                    </button>
                    <button
                      type="button"
                      className={`snap-btn snap-btn-generate-primary${
                        generatePulseActive ? " snap-btn-generate-primary--pulse" : ""
                      }`}
                      onClick={() => void handleGenerateClinicalNote()}
                      disabled={!generateEnabled || finalizingRecording}
                    >
                      <span className="snap-btn-icon">✦</span>
                      Generate Clinical Note
                    </button>
                    <button
                      type="button"
                      className="snap-btn snap-btn-rerecord-outline"
                      onClick={() => setRerecordConfirmOpen(true)}
                      disabled={!rerecordEnabled}
                    >
                      ↺ Re-record
                    </button>
                  </div>
                ) : null}

                {workflowState === "generated" ? (
                  <div className="snap-footer-generated-row">
                    <button
                      type="button"
                      className="snap-btn snap-btn-finalize-emr"
                      onClick={openFinalizeChartNoteAck}
                      disabled={!mergedNoteId || finalizeNoteBusy}
                    >
                      ⚡ Finalize Chart Note → Push to EMR
                    </button>
                    <button
                      type="button"
                      className="snap-btn snap-btn-record-more"
                      onClick={() => void handleStartOrResume({ mode: "recordMore" })}
                      disabled={finalizingRecording}
                    >
                      ▶ Record More
                    </button>
                    <button
                      type="button"
                      className="snap-btn snap-btn-rerecord-outline"
                      onClick={() => setRerecordConfirmOpen(true)}
                      disabled={!rerecordEnabled}
                    >
                      ↺ Re-record
                    </button>
                  </div>
                ) : null}
              </div>
            )}

            {rerecordConfirmOpen ? (
              <div
                className="snap-rerecord-popover"
                role="dialog"
                aria-modal="true"
                aria-labelledby="snap-rerecord-popover-title"
              >
                <p id="snap-rerecord-popover-title" className="snap-rerecord-popover-text">
                  Delete all {rerecordSegmentCount} segment{rerecordSegmentCount === 1 ? "" : "s"} and generated
                  note? This cannot be undone.
                </p>
                <div className="snap-rerecord-popover-actions">
                  <button
                    type="button"
                    className="snap-rerecord-popover-btn snap-rerecord-popover-btn--cancel"
                    onClick={() => setRerecordConfirmOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="snap-rerecord-popover-btn snap-rerecord-popover-btn--delete"
                    onClick={() => void confirmRerecord()}
                  >
                    Delete All
                  </button>
                </div>
              </div>
            ) : null}

            {/* <div className="snap-footer-note">{footerHint}</div> */}
          </div>
        </section>
      </div>

      {aiDownloadAckOpen ? (
        <div
          className="snap-ai-download-ack-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="snap-ai-download-ack-heading"
        >
          <div className="snap-ai-download-ack-dialog">
            <h2 id="snap-ai-download-ack-heading" className="snap-ai-download-ack-title">
              {AI_CHART_NOTE_DOWNLOAD_ACK_TITLE}
            </h2>
            <div className="snap-ai-download-ack-scroll">
              {AI_CHART_NOTE_DOWNLOAD_ACK_INTRO_PARAGRAPHS.map((p, i) => (
                <p key={`ack-intro-${i}`} className="snap-ai-download-ack-p">
                  {p}
                </p>
              ))}
              <ul className="snap-ai-download-ack-list">
                {AI_CHART_NOTE_DOWNLOAD_ACK_BULLETS.map((line, i) => (
                  <li key={`ack-bul-${i}`} className="snap-ai-download-ack-li">
                    {line}
                  </li>
                ))}
              </ul>
              {AI_CHART_NOTE_DOWNLOAD_ACK_CLOSING_PARAGRAPHS.map((p, i) => (
                <p key={`ack-close-${i}`} className="snap-ai-download-ack-p">
                  {p}
                </p>
              ))}
            </div>
            <label className="snap-ai-download-ack-check-row">
              <input
                type="checkbox"
                className="snap-ai-download-ack-checkbox"
                checked={aiDownloadAckChecked}
                onChange={(e) => setAiDownloadAckChecked(e.target.checked)}
              />
              <span className="snap-ai-download-ack-check-label">{AI_CHART_NOTE_DOWNLOAD_ACK_CHECKBOX_LABEL}</span>
            </label>
            <div className="snap-ai-download-ack-footer">
              <button type="button" className="snap-ai-download-ack-btn snap-ai-download-ack-btn-secondary" onClick={closeMergedNoteDownloadAck}>
                Cancel
              </button>
              <button
                type="button"
                className="snap-ai-download-ack-btn snap-ai-download-ack-btn-primary"
                disabled={!aiDownloadAckChecked || finalizeNoteBusy}
                onClick={() =>
                  void (aiAckPurpose === "finalize"
                    ? confirmFinalizeChartNoteAck()
                    : confirmMergedNoteDownloadAck())
                }
              >
                {aiAckPurpose === "finalize" ? "Finalize" : "Download"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {closeConfirmOpen ? (
        <Alert
          custonTitle="Close voice notes?"
          contentTitle="Any in-progress recording on this screen is discarded. Audio already uploaded may still transcribe in the background."
          type="warning"
          yesButtonName="OK"
          noButtonName="Cancel"
          onClose={() => setCloseConfirmOpen(false)}
          onDelete={confirmCloseModal}
        />
      ) : null}

    </div>
  );
}
